import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { authRegister, authLogin, dbLoad, dbSave } from "./db";

// ══════════════════════════════════════════════════════════════
//  미니 라우터 — react-router-dom 없이 URL 기반 SPA 라우팅
//  navigate(path)  : URL 변경 + 리렌더
//  useRouter()     : { pathname, navigate }
// ══════════════════════════════════════════════════════════════

function navigate(path) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new Event("pushstate"));
}

function useRouter() {
  const [pathname, setPathname] = useState(window.location.pathname);
  useEffect(() => {
    const sync = () => setPathname(window.location.pathname);
    window.addEventListener("popstate",  sync);
    window.addEventListener("pushstate", sync);
    return () => {
      window.removeEventListener("popstate",  sync);
      window.removeEventListener("pushstate", sync);
    };
  }, []);
  return { pathname, navigate };
}

/** "Neon Case" → "Neon-Case" */
function toSlug(name) { return name.replace(/\s+/g, "-"); }
/** "Neon-Case" → "Neon Case" */
function fromSlug(slug) { return slug.replace(/-/g, " "); }

// ══════════════════════════════════════════════════════════════
//  CS2 SKIN IMAGE
//  ByMykel CSGO-API → { skinName: imageUrl } 맵으로 관리
// ══════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════
//  STEAM API LAYER (프로토타입 시뮬레이션)
// ══════════════════════════════════════════════════════════════

function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

const STEAM_API = {
  getPlayerSummaries: async (steamId) => {
    await delay(800);
    return {
      steamid: steamId || "76561198000000000",
      personaname: "User#" + Math.floor(Math.random() * 9000 + 1000),
      avatarfull: null,
      profileurl: `https://steamcommunity.com/profiles/${steamId}`,
      personastate: 1,
      loccountrycode: "KR",
    };
  },
  getOwnedGames: async () => {
    await delay(600);
    return {
      game_count: 42,
      games: [{ appid: 730, name: "Counter-Strike 2", playtime_forever: 12400 }],
    };
  },
};

// ══════════════════════════════════════════════════════════════
//  결제 수단
// ══════════════════════════════════════════════════════════════

const PAYMENT_METHODS = [
  { id: "toss", name: "토스페이", icon: "💙", fee: 0 },
  { id: "kakao", name: "카카오페이", icon: "💛", fee: 0 },
  { id: "card", name: "신용/체크카드", icon: "💳", fee: 0 },
  { id: "bank", name: "계좌이체", icon: "🏦", fee: 0 },
];
const DEPOSIT_PRESETS = [5, 10, 20, 50, 100, 200];

// ══════════════════════════════════════════════════════════════
//  케이스 데이터 (emoji는 이미지 로드 실패 시 폴백)
//  item.name = Steam market_hash_name (Skinport 가격 fetch에도 사용)
//  이득률 5%~35% 6종 | EV = price × (1 - edge/100)
//  가격은 앱 로드 시 Skinport API로 실시간 업데이트
// ══════════════════════════════════════════════════════════════

/** item.name에서 표시용 이름 추출 (wear·★ 제거) */
function displayName(n) {
  return n.replace(/★\s*/g, "").replace(/\s*\([^)]*\)\s*$/, "").trim();
}

const BASE_CASES = [
  // ── Case 1: Neon Case  $3  5% edge  EV≈$2.85  8 items ──────
  {
    id: 1, name: "Neon Case", price: 3.00, image: "⚡", color: "#00e5ff", edge: 5,
    items: [
      { name: "★ Talon Knife | Doppler (Factory New)",          rarity: "covert",     color: "#eb4b4b", chance: 0.30,  price: 100.00, emoji: "⭐" },
      { name: "Desert Eagle | Blaze (Factory New)",             rarity: "classified", color: "#d32ce6", chance: 1.50,  price: 25.00,  emoji: "🔴" },
      { name: "Glock-18 | Gamma Doppler (Factory New)",         rarity: "restricted", color: "#8847ff", chance: 3.00,  price: 8.00,   emoji: "🟣" },
      { name: "USP-S | Orion (Field-Tested)",                   rarity: "restricted", color: "#8847ff", chance: 4.00,  price: 6.00,   emoji: "🟣" },
      { name: "AK-47 | Redline (Field-Tested)",                 rarity: "mil-spec",   color: "#4b69ff", chance: 4.00,  price: 8.00,   emoji: "🔵" },
      { name: "M4A1-S | Nightmare (Field-Tested)",              rarity: "mil-spec",   color: "#4b69ff", chance: 5.00,  price: 8.00,   emoji: "🔵" },
      { name: "M4A4 | Dragon King (Field-Tested)",              rarity: "consumer",   color: "#b0c3d9", chance: 10.00, price: 2.00,   emoji: "⬜" },
      { name: "AK-47 | Slate (Field-Tested)",                   rarity: "consumer",   color: "#b0c3d9", chance: 72.20, price: 1.07,   emoji: "⬜" },
    ],
  },
  // ── Case 2: Storm Case  $7  11% edge  EV≈$6.23  12 items ───
  {
    id: 2, name: "Storm Case", price: 7.00, image: "⛈️", color: "#4d79ff", edge: 11,
    items: [
      { name: "★ Stiletto Knife | Fade (Factory New)",          rarity: "covert",     color: "#eb4b4b", chance: 0.30,  price: 180.00, emoji: "⭐" },
      { name: "AK-47 | Fire Serpent (Field-Tested)",            rarity: "classified", color: "#d32ce6", chance: 0.60,  price: 80.00,  emoji: "🔴" },
      { name: "AWP | Hyper Beast (Field-Tested)",               rarity: "classified", color: "#d32ce6", chance: 1.00,  price: 35.00,  emoji: "🔴" },
      { name: "AK-47 | Vulcan (Field-Tested)",                  rarity: "classified", color: "#d32ce6", chance: 1.00,  price: 35.00,  emoji: "🔴" },
      { name: "M4A4 | The Emperor (Field-Tested)",              rarity: "restricted", color: "#8847ff", chance: 1.20,  price: 35.00,  emoji: "🟣" },
      { name: "AWP | Neo-Noir (Field-Tested)",                  rarity: "restricted", color: "#8847ff", chance: 1.50,  price: 30.00,  emoji: "🟣" },
      { name: "M4A1-S | Golden Coil (Field-Tested)",            rarity: "restricted", color: "#8847ff", chance: 2.50,  price: 15.00,  emoji: "🟣" },
      { name: "AK-47 | Bloodsport (Field-Tested)",              rarity: "mil-spec",   color: "#4b69ff", chance: 4.00,  price: 10.00,  emoji: "🔵" },
      { name: "Glock-18 | Gamma Doppler (Factory New)",         rarity: "mil-spec",   color: "#4b69ff", chance: 5.00,  price: 8.00,   emoji: "🔵" },
      { name: "AK-47 | Redline (Field-Tested)",                 rarity: "mil-spec",   color: "#4b69ff", chance: 6.00,  price: 8.00,   emoji: "🔵" },
      { name: "Desert Eagle | Cobalt Disruption (Field-Tested)",rarity: "consumer",   color: "#b0c3d9", chance: 10.00, price: 2.00,   emoji: "⬜" },
      { name: "AWP | Worm God (Field-Tested)",                  rarity: "consumer",   color: "#b0c3d9", chance: 66.90, price: 2.67,   emoji: "⬜" },
    ],
  },
  // ── Case 3: Inferno Case  $18  17% edge  EV≈$14.94  16 items
  {
    id: 3, name: "Inferno Case", price: 18.00, image: "🔥", color: "#ff6b35", edge: 17,
    items: [
      { name: "★ Karambit | Doppler (Factory New)",             rarity: "covert",     color: "#eb4b4b", chance: 0.15,  price: 500.00, emoji: "⭐" },
      { name: "AWP | Dragon Lore (Field-Tested)",               rarity: "covert",     color: "#eb4b4b", chance: 0.05,  price: 900.00, emoji: "⭐" },
      { name: "M4A4 | Howl (Field-Tested)",                     rarity: "covert",     color: "#eb4b4b", chance: 0.10,  price: 450.00, emoji: "⭐" },
      { name: "AK-47 | Wild Lotus (Field-Tested)",              rarity: "classified", color: "#d32ce6", chance: 0.12,  price: 350.00, emoji: "🔴" },
      { name: "AK-47 | Fire Serpent (Field-Tested)",            rarity: "classified", color: "#d32ce6", chance: 0.80,  price: 80.00,  emoji: "🔴" },
      { name: "AWP | Hyper Beast (Factory New)",                rarity: "classified", color: "#d32ce6", chance: 1.00,  price: 60.00,  emoji: "🔴" },
      { name: "M4A4 | The Emperor (Factory New)",               rarity: "restricted", color: "#8847ff", chance: 1.20,  price: 55.00,  emoji: "🟣" },
      { name: "AK-47 | Vulcan (Field-Tested)",                  rarity: "restricted", color: "#8847ff", chance: 1.80,  price: 35.00,  emoji: "🟣" },
      { name: "USP-S | Kill Confirmed (Field-Tested)",          rarity: "restricted", color: "#8847ff", chance: 1.50,  price: 45.00,  emoji: "🟣" },
      { name: "M4A1-S | Golden Coil (Field-Tested)",            rarity: "mil-spec",   color: "#4b69ff", chance: 3.50,  price: 15.00,  emoji: "🔵" },
      { name: "AWP | Asiimov (Field-Tested)",                   rarity: "mil-spec",   color: "#4b69ff", chance: 3.00,  price: 20.00,  emoji: "🔵" },
      { name: "AK-47 | Bloodsport (Field-Tested)",              rarity: "mil-spec",   color: "#4b69ff", chance: 5.00,  price: 10.00,  emoji: "🔵" },
      { name: "Glock-18 | Gamma Doppler (Factory New)",         rarity: "mil-spec",   color: "#4b69ff", chance: 5.00,  price: 8.00,   emoji: "🔵" },
      { name: "Desert Eagle | Blaze (Factory New)",             rarity: "mil-spec",   color: "#4b69ff", chance: 2.00,  price: 25.00,  emoji: "🔵" },
      { name: "M4A1-S | Nightmare (Field-Tested)",              rarity: "consumer",   color: "#b0c3d9", chance: 6.00,  price: 8.00,   emoji: "⬜" },
      { name: "AK-47 | Redline (Field-Tested)",                 rarity: "consumer",   color: "#b0c3d9", chance: 68.78, price: 9.68,   emoji: "⬜" },
    ],
  },
  // ── Case 4: Cobalt Case  $40  23% edge  EV≈$30.80  20 items
  {
    id: 4, name: "Cobalt Case", price: 40.00, image: "💎", color: "#1e90ff", edge: 23,
    items: [
      { name: "★ Butterfly Knife | Fade (Factory New)",         rarity: "covert",     color: "#eb4b4b", chance: 0.10,  price: 700.00, emoji: "⭐" },
      { name: "★ Karambit | Fade (Factory New)",                rarity: "covert",     color: "#eb4b4b", chance: 0.10,  price: 700.00, emoji: "⭐" },
      { name: "AWP | Dragon Lore (Field-Tested)",               rarity: "covert",     color: "#eb4b4b", chance: 0.08,  price: 900.00, emoji: "⭐" },
      { name: "M4A4 | Howl (Field-Tested)",                     rarity: "classified", color: "#d32ce6", chance: 0.15,  price: 450.00, emoji: "🔴" },
      { name: "AK-47 | Wild Lotus (Field-Tested)",              rarity: "classified", color: "#d32ce6", chance: 0.18,  price: 350.00, emoji: "🔴" },
      { name: "AK-47 | Fire Serpent (Field-Tested)",            rarity: "classified", color: "#d32ce6", chance: 1.00,  price: 80.00,  emoji: "🔴" },
      { name: "AWP | Hyper Beast (Factory New)",                rarity: "restricted", color: "#8847ff", chance: 1.50,  price: 60.00,  emoji: "🟣" },
      { name: "USP-S | Kill Confirmed (Field-Tested)",          rarity: "restricted", color: "#8847ff", chance: 2.00,  price: 45.00,  emoji: "🟣" },
      { name: "M4A4 | The Emperor (Factory New)",               rarity: "restricted", color: "#8847ff", chance: 1.50,  price: 55.00,  emoji: "🟣" },
      { name: "AK-47 | Vulcan (Field-Tested)",                  rarity: "restricted", color: "#8847ff", chance: 2.50,  price: 35.00,  emoji: "🟣" },
      { name: "AWP | Neo-Noir (Field-Tested)",                  rarity: "mil-spec",   color: "#4b69ff", chance: 2.50,  price: 35.00,  emoji: "🔵" },
      { name: "M4A1-S | Golden Coil (Field-Tested)",            rarity: "mil-spec",   color: "#4b69ff", chance: 4.00,  price: 15.00,  emoji: "🔵" },
      { name: "AWP | Asiimov (Field-Tested)",                   rarity: "mil-spec",   color: "#4b69ff", chance: 3.50,  price: 20.00,  emoji: "🔵" },
      { name: "AK-47 | Bloodsport (Field-Tested)",              rarity: "mil-spec",   color: "#4b69ff", chance: 6.00,  price: 10.00,  emoji: "🔵" },
      { name: "Desert Eagle | Blaze (Factory New)",             rarity: "mil-spec",   color: "#4b69ff", chance: 3.00,  price: 25.00,  emoji: "🔵" },
      { name: "M4A1-S | Nightmare (Field-Tested)",              rarity: "consumer",   color: "#b0c3d9", chance: 5.00,  price: 8.00,   emoji: "⬜" },
      { name: "AK-47 | Redline (Field-Tested)",                 rarity: "consumer",   color: "#b0c3d9", chance: 5.00,  price: 8.00,   emoji: "⬜" },
      { name: "Glock-18 | Gamma Doppler (Factory New)",         rarity: "consumer",   color: "#b0c3d9", chance: 5.00,  price: 8.00,   emoji: "⬜" },
      { name: "USP-S | Orion (Field-Tested)",                   rarity: "consumer",   color: "#b0c3d9", chance: 6.00,  price: 6.00,   emoji: "⬜" },
      { name: "M4A4 | Buzz Kill (Field-Tested)",                rarity: "consumer",   color: "#b0c3d9", chance: 50.89, price: 35.35,  emoji: "⬜" },
    ],
  },
  // ── Case 5: Titan Case  $80  29% edge  EV≈$56.80  22 items ─
  {
    id: 5, name: "Titan Case", price: 80.00, image: "👑", color: "#9b59b6", edge: 29,
    items: [
      { name: "★ Karambit | Gamma Doppler (Factory New)",       rarity: "covert",     color: "#eb4b4b", chance: 0.20,  price: 700.00,  emoji: "⭐" },
      { name: "AWP | Dragon Lore (Factory New)",                rarity: "covert",     color: "#eb4b4b", chance: 0.08,  price: 1800.00, emoji: "⭐" },
      { name: "M4A4 | Howl (Factory New)",                      rarity: "covert",     color: "#eb4b4b", chance: 0.10,  price: 900.00,  emoji: "⭐" },
      { name: "AK-47 | Wild Lotus (Factory New)",               rarity: "classified", color: "#d32ce6", chance: 0.12,  price: 600.00,  emoji: "🔴" },
      { name: "★ Butterfly Knife | Marble Fade (Factory New)",  rarity: "classified", color: "#d32ce6", chance: 0.12,  price: 600.00,  emoji: "🔴" },
      { name: "★ M9 Bayonet | Doppler (Factory New)",           rarity: "classified", color: "#d32ce6", chance: 0.30,  price: 220.00,  emoji: "🔴" },
      { name: "AK-47 | Fire Serpent (Factory New)",             rarity: "classified", color: "#d32ce6", chance: 0.40,  price: 150.00,  emoji: "🔴" },
      { name: "USP-S | Kill Confirmed (Factory New)",           rarity: "restricted", color: "#8847ff", chance: 0.70,  price: 100.00,  emoji: "🟣" },
      { name: "AWP | Hyper Beast (Factory New)",                rarity: "restricted", color: "#8847ff", chance: 1.00,  price: 60.00,   emoji: "🟣" },
      { name: "M4A4 | The Emperor (Factory New)",               rarity: "restricted", color: "#8847ff", chance: 1.20,  price: 55.00,   emoji: "🟣" },
      { name: "AK-47 | Vulcan (Field-Tested)",                  rarity: "mil-spec",   color: "#4b69ff", chance: 2.00,  price: 35.00,   emoji: "🔵" },
      { name: "AWP | Neo-Noir (Field-Tested)",                  rarity: "mil-spec",   color: "#4b69ff", chance: 2.00,  price: 35.00,   emoji: "🔵" },
      { name: "Desert Eagle | Blaze (Factory New)",             rarity: "mil-spec",   color: "#4b69ff", chance: 3.00,  price: 25.00,   emoji: "🔵" },
      { name: "AWP | Asiimov (Field-Tested)",                   rarity: "mil-spec",   color: "#4b69ff", chance: 3.50,  price: 20.00,   emoji: "🔵" },
      { name: "M4A1-S | Golden Coil (Field-Tested)",            rarity: "mil-spec",   color: "#4b69ff", chance: 5.00,  price: 15.00,   emoji: "🔵" },
      { name: "AK-47 | Bloodsport (Field-Tested)",              rarity: "consumer",   color: "#b0c3d9", chance: 7.00,  price: 10.00,   emoji: "⬜" },
      { name: "M4A1-S | Nightmare (Field-Tested)",              rarity: "consumer",   color: "#b0c3d9", chance: 8.00,  price: 8.00,    emoji: "⬜" },
      { name: "AK-47 | Redline (Field-Tested)",                 rarity: "consumer",   color: "#b0c3d9", chance: 8.00,  price: 8.00,    emoji: "⬜" },
      { name: "Glock-18 | Gamma Doppler (Factory New)",         rarity: "consumer",   color: "#b0c3d9", chance: 8.00,  price: 8.00,    emoji: "⬜" },
      { name: "USP-S | Orion (Field-Tested)",                   rarity: "consumer",   color: "#b0c3d9", chance: 9.00,  price: 6.00,    emoji: "⬜" },
      { name: "AK-47 | Frontside Misty (Field-Tested)",         rarity: "consumer",   color: "#b0c3d9", chance: 8.00,  price: 2.00,    emoji: "⬜" },
      { name: "★ Talon Knife | Doppler (Factory New)",          rarity: "covert",     color: "#eb4b4b", chance: 32.28, price: 128.50,  emoji: "⭐" },
    ],
  },
  // ── Case 7: Shadow Case  $1.50  8% edge  EV≈$1.38  8 items ─
  {
    id: 7, name: "Shadow Case", price: 1.50, image: "🌙", color: "#546e7a", edge: 8,
    items: [
      { name: "★ Shadow Daggers | Stained (Field-Tested)",      rarity: "covert",     color: "#eb4b4b", chance: 0.25,  price: 45.00, emoji: "⭐" },
      { name: "P250 | Whiteout (Factory New)",                   rarity: "classified", color: "#d32ce6", chance: 1.20,  price: 9.00,  emoji: "🔴" },
      { name: "Five-SeveN | Fowl Play (Factory New)",            rarity: "restricted", color: "#8847ff", chance: 2.50,  price: 5.00,  emoji: "🟣" },
      { name: "Tec-9 | Fuel Injector (Field-Tested)",            rarity: "restricted", color: "#8847ff", chance: 3.50,  price: 3.00,  emoji: "🟣" },
      { name: "MP9 | Hot Rod (Factory New)",                     rarity: "mil-spec",   color: "#4b69ff", chance: 5.00,  price: 2.00,  emoji: "🔵" },
      { name: "MAC-10 | Neon Rider (Field-Tested)",              rarity: "mil-spec",   color: "#4b69ff", chance: 6.00,  price: 1.50,  emoji: "🔵" },
      { name: "CZ75-Auto | Pole Position (Field-Tested)",        rarity: "consumer",   color: "#b0c3d9", chance: 9.00,  price: 0.60,  emoji: "⬜" },
      { name: "P2000 | Oceanic (Field-Tested)",                  rarity: "consumer",   color: "#b0c3d9", chance: 72.55, price: 0.90,  emoji: "⬜" },
    ],
  },
  // ── Case 8: Glacier Case  $12  14% edge  EV≈$10.32  12 items
  {
    id: 8, name: "Glacier Case", price: 12.00, image: "❄️", color: "#64b5f6", edge: 14,
    items: [
      { name: "★ Huntsman Knife | Doppler (Factory New)",        rarity: "covert",     color: "#eb4b4b", chance: 0.25,  price: 260.00, emoji: "⭐" },
      { name: "AWP | BOOM (Factory New)",                        rarity: "classified", color: "#d32ce6", chance: 0.60,  price: 55.00,  emoji: "🔴" },
      { name: "M4A1-S | Cyrex (Field-Tested)",                   rarity: "classified", color: "#d32ce6", chance: 1.20,  price: 22.00,  emoji: "🔴" },
      { name: "Desert Eagle | Printstream (Field-Tested)",        rarity: "restricted", color: "#8847ff", chance: 2.00,  price: 18.00,  emoji: "🟣" },
      { name: "Glock-18 | Water Elemental (Factory New)",         rarity: "restricted", color: "#8847ff", chance: 2.50,  price: 12.00,  emoji: "🟣" },
      { name: "AK-47 | Neon Revolution (Field-Tested)",           rarity: "restricted", color: "#8847ff", chance: 3.00,  price: 10.00,  emoji: "🟣" },
      { name: "M4A4 | Desolate Space (Field-Tested)",             rarity: "mil-spec",   color: "#4b69ff", chance: 4.00,  price: 5.00,   emoji: "🔵" },
      { name: "USP-S | Neo-Noir (Field-Tested)",                  rarity: "mil-spec",   color: "#4b69ff", chance: 5.00,  price: 4.00,   emoji: "🔵" },
      { name: "MP5-SD | Lab Rats (Field-Tested)",                 rarity: "mil-spec",   color: "#4b69ff", chance: 6.00,  price: 2.00,   emoji: "🔵" },
      { name: "Five-SeveN | Angry Mob (Field-Tested)",            rarity: "consumer",   color: "#b0c3d9", chance: 8.00,  price: 1.20,   emoji: "⬜" },
      { name: "MAC-10 | Silver (Field-Tested)",                   rarity: "consumer",   color: "#b0c3d9", chance: 10.00, price: 0.80,   emoji: "⬜" },
      { name: "Tec-9 | Urban DDPAT (Field-Tested)",               rarity: "consumer",   color: "#b0c3d9", chance: 57.45, price: 3.48,   emoji: "⬜" },
    ],
  },
  // ── Case 9: Crimson Case  $25  20% edge  EV≈$20.00  14 items
  {
    id: 9, name: "Crimson Case", price: 25.00, image: "🩸", color: "#e53935", edge: 20,
    items: [
      { name: "★ Ursus Knife | Doppler (Factory New)",           rarity: "covert",     color: "#eb4b4b", chance: 0.20,  price: 280.00, emoji: "⭐" },
      { name: "★ Navaja Knife | Fade (Factory New)",             rarity: "covert",     color: "#eb4b4b", chance: 0.30,  price: 120.00, emoji: "⭐" },
      { name: "AK-47 | The Empress (Field-Tested)",              rarity: "classified", color: "#d32ce6", chance: 0.50,  price: 65.00,  emoji: "🔴" },
      { name: "AWP | Medusa (Field-Tested)",                     rarity: "classified", color: "#d32ce6", chance: 0.80,  price: 55.00,  emoji: "🔴" },
      { name: "M4A4 | Spider Lily (Field-Tested)",               rarity: "restricted", color: "#8847ff", chance: 1.50,  price: 28.00,  emoji: "🟣" },
      { name: "Glock-18 | Wasteland Rebel (Field-Tested)",        rarity: "restricted", color: "#8847ff", chance: 2.00,  price: 16.00,  emoji: "🟣" },
      { name: "M4A1-S | Icarus Fell (Field-Tested)",              rarity: "restricted", color: "#8847ff", chance: 2.50,  price: 12.00,  emoji: "🟣" },
      { name: "Desert Eagle | Trigger Discipline (Field-Tested)", rarity: "mil-spec",   color: "#4b69ff", chance: 3.50,  price: 7.00,   emoji: "🔵" },
      { name: "AK-47 | Neon Revolution (Field-Tested)",           rarity: "mil-spec",   color: "#4b69ff", chance: 4.50,  price: 10.00,  emoji: "🔵" },
      { name: "MP7 | Fade (Factory New)",                         rarity: "mil-spec",   color: "#4b69ff", chance: 5.00,  price: 5.00,   emoji: "🔵" },
      { name: "USP-S | Road Rash (Field-Tested)",                 rarity: "mil-spec",   color: "#4b69ff", chance: 5.00,  price: 4.00,   emoji: "🔵" },
      { name: "Five-SeveN | Monkey Business (Field-Tested)",      rarity: "consumer",   color: "#b0c3d9", chance: 7.00,  price: 1.00,   emoji: "⬜" },
      { name: "CZ75-Auto | Victoria (Field-Tested)",              rarity: "consumer",   color: "#b0c3d9", chance: 8.00,  price: 1.20,   emoji: "⬜" },
      { name: "P90 | Death Grip (Field-Tested)",                  rarity: "consumer",   color: "#b0c3d9", chance: 59.20, price: 6.82,   emoji: "⬜" },
    ],
  },
  // ── Case 10: Phantom Case  $60  26% edge  EV≈$44.40  17 items
  {
    id: 10, name: "Phantom Case", price: 60.00, image: "👻", color: "#7c4dff", edge: 26,
    items: [
      { name: "★ Skeleton Knife | Fade (Factory New)",            rarity: "covert",     color: "#eb4b4b", chance: 0.15,  price: 700.00, emoji: "⭐" },
      { name: "★ Nomad Knife | Doppler (Factory New)",            rarity: "covert",     color: "#eb4b4b", chance: 0.20,  price: 350.00, emoji: "⭐" },
      { name: "AWP | Medusa (Factory New)",                       rarity: "classified", color: "#d32ce6", chance: 0.40,  price: 160.00, emoji: "🔴" },
      { name: "AK-47 | The Empress (Factory New)",                rarity: "classified", color: "#d32ce6", chance: 0.60,  price: 100.00, emoji: "🔴" },
      { name: "M4A4 | Spider Lily (Factory New)",                 rarity: "classified", color: "#d32ce6", chance: 0.80,  price: 80.00,  emoji: "🔴" },
      { name: "Desert Eagle | Printstream (Factory New)",          rarity: "restricted", color: "#8847ff", chance: 1.50,  price: 45.00,  emoji: "🟣" },
      { name: "AWP | Lightning Strike (Factory New)",              rarity: "restricted", color: "#8847ff", chance: 1.80,  price: 38.00,  emoji: "🟣" },
      { name: "M4A1-S | Welcome to the Jungle (Field-Tested)",    rarity: "restricted", color: "#8847ff", chance: 2.50,  price: 22.00,  emoji: "🟣" },
      { name: "Glock-18 | Twilight Galaxy (Field-Tested)",         rarity: "mil-spec",   color: "#4b69ff", chance: 3.00,  price: 14.00,  emoji: "🔵" },
      { name: "USP-S | Printstream (Field-Tested)",               rarity: "mil-spec",   color: "#4b69ff", chance: 3.50,  price: 25.00,  emoji: "🔵" },
      { name: "AK-47 | Neon Rider (Field-Tested)",                rarity: "mil-spec",   color: "#4b69ff", chance: 4.00,  price: 12.00,  emoji: "🔵" },
      { name: "MP7 | Nemesis (Field-Tested)",                     rarity: "mil-spec",   color: "#4b69ff", chance: 4.00,  price: 5.00,   emoji: "🔵" },
      { name: "P90 | Asiimov (Field-Tested)",                     rarity: "consumer",   color: "#b0c3d9", chance: 5.00,  price: 3.50,   emoji: "⬜" },
      { name: "MAC-10 | Neon Rider (Field-Tested)",               rarity: "consumer",   color: "#b0c3d9", chance: 6.00,  price: 1.50,   emoji: "⬜" },
      { name: "M4A4 | In Living Color (Field-Tested)",            rarity: "consumer",   color: "#b0c3d9", chance: 6.00,  price: 4.00,   emoji: "⬜" },
      { name: "AK-47 | Safari Mesh (Field-Tested)",               rarity: "consumer",   color: "#b0c3d9", chance: 7.00,  price: 0.50,   emoji: "⬜" },
      { name: "★ Paracord Knife | Stained (Field-Tested)",        rarity: "covert",     color: "#eb4b4b", chance: 53.55, price: 95.40,  emoji: "⭐" },
    ],
  },
  // ── Case 6: Apex Case  $150  35% edge  EV≈$97.50  25 items ─
  {
    id: 6, name: "Apex Case", price: 150.00, image: "🏆", color: "#f5a623", edge: 35,
    items: [
      { name: "★ Karambit | Fade (Factory New)",                rarity: "covert",     color: "#eb4b4b", chance: 0.15,  price: 800.00,  emoji: "⭐" },
      { name: "★ Butterfly Knife | Fade (Factory New)",         rarity: "covert",     color: "#eb4b4b", chance: 0.10,  price: 900.00,  emoji: "⭐" },
      { name: "★ Karambit | Gamma Doppler (Factory New)",       rarity: "covert",     color: "#eb4b4b", chance: 0.12,  price: 750.00,  emoji: "⭐" },
      { name: "AWP | Dragon Lore (Factory New)",                rarity: "covert",     color: "#eb4b4b", chance: 0.05,  price: 1800.00, emoji: "⭐" },
      { name: "M4A4 | Howl (Factory New)",                      rarity: "covert",     color: "#eb4b4b", chance: 0.08,  price: 900.00,  emoji: "⭐" },
      { name: "AK-47 | Wild Lotus (Factory New)",               rarity: "classified", color: "#d32ce6", chance: 0.12,  price: 600.00,  emoji: "🔴" },
      { name: "★ M9 Bayonet | Fade (Factory New)",              rarity: "classified", color: "#d32ce6", chance: 0.18,  price: 350.00,  emoji: "🔴" },
      { name: "★ M9 Bayonet | Doppler (Factory New)",           rarity: "classified", color: "#d32ce6", chance: 0.28,  price: 220.00,  emoji: "🔴" },
      { name: "AK-47 | Fire Serpent (Factory New)",             rarity: "classified", color: "#d32ce6", chance: 0.45,  price: 150.00,  emoji: "🔴" },
      { name: "USP-S | Kill Confirmed (Factory New)",           rarity: "restricted", color: "#8847ff", chance: 0.70,  price: 100.00,  emoji: "🟣" },
      { name: "AWP | Hyper Beast (Factory New)",                rarity: "restricted", color: "#8847ff", chance: 1.20,  price: 60.00,   emoji: "🟣" },
      { name: "M4A4 | The Emperor (Factory New)",               rarity: "restricted", color: "#8847ff", chance: 1.50,  price: 55.00,   emoji: "🟣" },
      { name: "AK-47 | Vulcan (Field-Tested)",                  rarity: "mil-spec",   color: "#4b69ff", chance: 2.00,  price: 35.00,   emoji: "🔵" },
      { name: "AWP | Neo-Noir (Field-Tested)",                  rarity: "mil-spec",   color: "#4b69ff", chance: 2.00,  price: 35.00,   emoji: "🔵" },
      { name: "Desert Eagle | Blaze (Factory New)",             rarity: "mil-spec",   color: "#4b69ff", chance: 3.00,  price: 25.00,   emoji: "🔵" },
      { name: "AWP | Asiimov (Field-Tested)",                   rarity: "mil-spec",   color: "#4b69ff", chance: 3.50,  price: 20.00,   emoji: "🔵" },
      { name: "M4A1-S | Golden Coil (Field-Tested)",            rarity: "mil-spec",   color: "#4b69ff", chance: 5.00,  price: 15.00,   emoji: "🔵" },
      { name: "AK-47 | Bloodsport (Field-Tested)",              rarity: "consumer",   color: "#b0c3d9", chance: 6.00,  price: 10.00,   emoji: "⬜" },
      { name: "M4A1-S | Nightmare (Field-Tested)",              rarity: "consumer",   color: "#b0c3d9", chance: 6.50,  price: 8.00,    emoji: "⬜" },
      { name: "AK-47 | Redline (Field-Tested)",                 rarity: "consumer",   color: "#b0c3d9", chance: 7.00,  price: 8.00,    emoji: "⬜" },
      { name: "Glock-18 | Gamma Doppler (Factory New)",         rarity: "consumer",   color: "#b0c3d9", chance: 6.50,  price: 8.00,    emoji: "⬜" },
      { name: "USP-S | Orion (Field-Tested)",                   rarity: "consumer",   color: "#b0c3d9", chance: 7.00,  price: 6.00,    emoji: "⬜" },
      { name: "AK-47 | Frontside Misty (Field-Tested)",         rarity: "consumer",   color: "#b0c3d9", chance: 7.00,  price: 2.00,    emoji: "⬜" },
      { name: "M4A4 | Dragon King (Field-Tested)",              rarity: "consumer",   color: "#b0c3d9", chance: 6.00,  price: 2.00,    emoji: "⬜" },
      { name: "★ M9 Bayonet | Slaughter (Factory New)",         rarity: "covert",     color: "#eb4b4b", chance: 33.57, price: 242.82,  emoji: "⭐" },
    ],
  },

  // ── Sticker Case 1: Fan Favorites Capsule  $2  10% edge  8 items ─
  {
    id: 11, name: "Fan Favorites Capsule", price: 2.00, image: "🎭", color: "#ff7043", edge: 10, type: "sticker",
    items: [
      { name: "Sticker | Crown (Foil)",                             rarity: "covert",     color: "#eb4b4b", chance: 0.30,  price: 120.00, emoji: "⭐" },
      { name: "Sticker | Flammable (Foil)",                         rarity: "classified", color: "#d32ce6", chance: 1.50,  price: 18.00,  emoji: "🔴" },
      { name: "Sticker | Natus Vincere (Holo) | Paris 2023",        rarity: "restricted", color: "#8847ff", chance: 3.00,  price: 6.00,   emoji: "🟣" },
      { name: "Sticker | FaZe Clan (Holo) | Paris 2023",            rarity: "restricted", color: "#8847ff", chance: 4.00,  price: 4.00,   emoji: "🟣" },
      { name: "Sticker | G2 Esports (Foil) | Paris 2023",           rarity: "mil-spec",   color: "#4b69ff", chance: 5.00,  price: 2.50,   emoji: "🔵" },
      { name: "Sticker | Vitality (Holo) | Paris 2023",             rarity: "mil-spec",   color: "#4b69ff", chance: 8.00,  price: 1.50,   emoji: "🔵" },
      { name: "Sticker | Fnatic (Holo) | Paris 2023",               rarity: "consumer",   color: "#b0c3d9", chance: 10.00, price: 1.20,   emoji: "⬜" },
      { name: "Sticker | Banana",                                   rarity: "consumer",   color: "#b0c3d9", chance: 68.20, price: 0.80,   emoji: "⬜" },
    ],
  },

  // ── Sticker Case 2: Holo Dreams Capsule  $6  15% edge  10 items ─
  {
    id: 12, name: "Holo Dreams Capsule", price: 6.00, image: "✨", color: "#ab47bc", edge: 15, type: "sticker",
    items: [
      { name: "Sticker | iBUYPOWER (Holo) | Katowice 2014",        rarity: "covert",     color: "#eb4b4b", chance: 0.05,  price: 8000.00, emoji: "⭐" },
      { name: "Sticker | Crown (Foil)",                             rarity: "classified", color: "#d32ce6", chance: 0.20,  price: 120.00,  emoji: "🔴" },
      { name: "Sticker | Fnatic (Holo) | Katowice 2014",            rarity: "classified", color: "#d32ce6", chance: 0.50,  price: 60.00,   emoji: "🔴" },
      { name: "Sticker | Flammable (Foil)",                         rarity: "restricted", color: "#8847ff", chance: 1.50,  price: 18.00,   emoji: "🟣" },
      { name: "Sticker | Boom (Holo)",                              rarity: "restricted", color: "#8847ff", chance: 3.00,  price: 8.00,    emoji: "🟣" },
      { name: "Sticker | Phoenix (Foil)",                           rarity: "mil-spec",   color: "#4b69ff", chance: 4.50,  price: 5.00,    emoji: "🔵" },
      { name: "Sticker | Headless Chicken",                         rarity: "mil-spec",   color: "#4b69ff", chance: 6.00,  price: 3.00,    emoji: "🔵" },
      { name: "Sticker | Chicken Lover",                            rarity: "consumer",   color: "#b0c3d9", chance: 8.00,  price: 2.00,    emoji: "⬜" },
      { name: "Sticker | Banana",                                   rarity: "consumer",   color: "#b0c3d9", chance: 10.00, price: 1.00,    emoji: "⬜" },
      { name: "Sticker | Natus Vincere (Holo) | Paris 2023",        rarity: "consumer",   color: "#b0c3d9", chance: 66.25, price: 1.50,    emoji: "⬜" },
    ],
  },

  // ── Sticker Case 3: Gold Standard Capsule  $15  18% edge  12 items ─
  {
    id: 13, name: "Gold Standard Capsule", price: 15.00, image: "🥇", color: "#ffd700", edge: 18, type: "sticker",
    items: [
      { name: "Sticker | iBUYPOWER (Holo) | Katowice 2014",        rarity: "covert",     color: "#eb4b4b", chance: 0.03,  price: 8000.00, emoji: "⭐" },
      { name: "Sticker | Virtus.Pro (Holo) | Katowice 2014",        rarity: "covert",     color: "#eb4b4b", chance: 0.07,  price: 400.00,  emoji: "⭐" },
      { name: "Sticker | Crown (Foil)",                             rarity: "classified", color: "#d32ce6", chance: 0.10,  price: 120.00,  emoji: "🔴" },
      { name: "Sticker | Fnatic (Holo) | Katowice 2014",            rarity: "classified", color: "#d32ce6", chance: 0.30,  price: 60.00,   emoji: "🔴" },
      { name: "Sticker | Flammable (Foil)",                         rarity: "restricted", color: "#8847ff", chance: 1.00,  price: 18.00,   emoji: "🟣" },
      { name: "Sticker | Natus Vincere (Gold) | Paris 2023",        rarity: "restricted", color: "#8847ff", chance: 2.00,  price: 12.00,   emoji: "🟣" },
      { name: "Sticker | FaZe Clan (Gold) | Paris 2023",            rarity: "restricted", color: "#8847ff", chance: 3.00,  price: 10.00,   emoji: "🟣" },
      { name: "Sticker | Boom (Holo)",                              rarity: "mil-spec",   color: "#4b69ff", chance: 5.00,  price: 8.00,    emoji: "🔵" },
      { name: "Sticker | Phoenix (Foil)",                           rarity: "mil-spec",   color: "#4b69ff", chance: 7.00,  price: 5.00,    emoji: "🔵" },
      { name: "Sticker | Poorly Drawn Chicken (Holo)",              rarity: "mil-spec",   color: "#4b69ff", chance: 7.00,  price: 3.00,    emoji: "🔵" },
      { name: "Sticker | Chicken Lover",                            rarity: "consumer",   color: "#b0c3d9", chance: 10.00, price: 2.00,    emoji: "⬜" },
      { name: "Sticker | Banana",                                   rarity: "consumer",   color: "#b0c3d9", chance: 64.50, price: 1.80,    emoji: "⬜" },
    ],
  },
];

const RARITY_LABEL = {
  covert: "★ 코버트",
  classified: "클래시파이드",
  restricted: "리스트릭티드",
  "mil-spec": "밀스펙",
  consumer: "컨슈머",
};

function weightedRandom(items) {
  const total = items.reduce((s, i) => s + i.chance, 0);
  let r = Math.random() * total;
  for (const item of items) {
    r -= item.chance;
    if (r <= 0) return item;
  }
  return items[items.length - 1];
}

/** 케이스 가격의 6배 이상 아이템 = "자동차" 아이템 */
function isCarItem(item, casePrice) {
  return item.price >= casePrice * 5;
}

// ══════════════════════════════════════════════════════════════
//  DB / 인증 — Supabase (db.js)
// ══════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════
//  스킨 이미지 컴포넌트
// ══════════════════════════════════════════════════════════════

/** 이미지 or 이모지 폴백. isCar=true 이면 무조건 🚗 */
function SkinImage({ item, imageMap, size = 72, style = {}, isCar = false }) {
  // imageMap: ByMykel CSGO-API에서 로딩된 스킨 이미지 URL
  const src = imageMap?.[item.name] || null;
  const [imgReady, setImgReady] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);

  // src가 바뀌면(imageMap 로드 완료 등) 상태 초기화
  useEffect(() => {
    setImgReady(false);
    setImgFailed(false);
  }, [src]);

  // 자동차 아이템은 🚗 이모지로 강제 표시
  if (isCar) {
    return (
      <span style={{ fontSize: size * 0.55, lineHeight: 1, filter: "drop-shadow(0 0 8px #ffd700)", ...style }}>
        🚗
      </span>
    );
  }

  // 이미지 없거나 실패 → 이모지 폴백
  if (!src || imgFailed) {
    return (
      <span style={{ fontSize: size * 0.5, lineHeight: 1, ...style }}>
        {item.emoji}
      </span>
    );
  }

  // 이미지 로딩 중엔 이모지 표시, 로드 완료 후 실제 이미지로 전환
  return (
    <>
      {!imgReady && (
        <span style={{ fontSize: size * 0.5, lineHeight: 1 }}>
          {item.emoji}
        </span>
      )}
      <img
        src={src}
        alt={item.name}
        onLoad={() => setImgReady(true)}
        onError={() => setImgFailed(true)}
        style={{
          display: imgReady ? "block" : "none",
          width: size,
          height: size * 0.7,
          objectFit: "contain",
          filter: "drop-shadow(0 2px 6px rgba(0,0,0,.6))",
          ...style,
        }}
      />
    </>
  );
}

// ══════════════════════════════════════════════════════════════
//  로그인 / 회원가입 모달
// ══════════════════════════════════════════════════════════════

function LoginRegisterModal({ onClose, onLoginSuccess }) {
  const [tab, setTab] = useState("login"); // "login" | "register"
  const [form, setForm] = useState({ email: "", username: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [popup, setPopup] = useState(null); // 2초 팝업 { msg, color }

  const showPopup = (msg, color = "#f85149") => {
    setPopup({ msg, color });
    setTimeout(() => setPopup(null), 2000);
  };

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setError("");
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    await delay(400);
    const res = await authLogin({ username: form.username, password: form.password });
    setLoading(false);
    if (!res.ok) { setError(res.msg); return; }
    onLoginSuccess(res.record);
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { setError("비밀번호가 일치하지 않습니다."); return; }
    setLoading(true);
    setError("");
    await delay(400);
    const res = await authRegister({ email: form.email, username: form.username, password: form.password });
    setLoading(false);
    if (!res.ok) {
      // 아이디·이메일 중복 → 2초 팝업
      if (res.dupType) { showPopup(res.msg); return; }
      // 그 외 유효성 오류 → 인라인 에러
      setError(res.msg);
      return;
    }
    setSuccessMsg("회원가입 완료! 로그인해 주세요.");
    setTab("login");
    setForm((f) => ({ ...f, email: "", confirm: "", password: "" }));
    setTimeout(() => setSuccessMsg(""), 4000);
  };

  const inputStyle = {
    width: "100%", background: "#0d1117", border: "1px solid #30363d",
    borderRadius: 8, color: "#e6edf3", padding: "10px 14px", fontSize: 14,
    outline: "none", boxSizing: "border-box", transition: "border-color .2s",
  };
  const labelStyle = { display: "block", color: "#8b949e", fontSize: 12, marginBottom: 6, fontWeight: 600 };
  const btnPrimary = {
    width: "100%", background: loading ? "#30363d" : "linear-gradient(90deg,#ff6b35,#f7931e)",
    border: "none", borderRadius: 8, color: loading ? "#8b949e" : "#fff",
    padding: "11px 0", fontSize: 15, fontWeight: 700,
    cursor: loading ? "not-allowed" : "pointer", marginTop: 8, transition: "all .2s",
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.75)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* 2초 팝업 토스트 */}
      {popup && (
        <div style={{
          position: "fixed", top: 32, left: "50%", transform: "translateX(-50%)",
          zIndex: 600, background: "#1c2128", border: `2px solid ${popup.color}`,
          borderRadius: 12, padding: "14px 28px", color: popup.color,
          fontWeight: 700, fontSize: 15, boxShadow: `0 8px 32px ${popup.color}55`,
          animation: "slideIn .2s ease", whiteSpace: "nowrap",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          ⚠️ {popup.msg}
        </div>
      )}

      <div style={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 16, width: "100%", maxWidth: 420, padding: "32px 28px", boxShadow: "0 20px 60px rgba(0,0,0,.7)", animation: "fadeIn .25s ease" }}>

        {/* 헤더 */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div style={{ fontWeight: 900, fontSize: 20, background: "linear-gradient(90deg,#ff6b35,#f7c59f)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            🚗 MotorcarCase
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#8b949e", fontSize: 20, cursor: "pointer", lineHeight: 1, padding: 4 }}>✕</button>
        </div>

        {/* 탭 */}
        <div style={{ display: "flex", gap: 0, marginBottom: 24, background: "#0d1117", borderRadius: 10, padding: 4 }}>
          {[["login", "🔑 로그인"], ["register", "✏️ 회원가입"]].map(([t, label]) => (
            <button
              key={t}
              onClick={() => { setTab(t); setError(""); setSuccessMsg(""); }}
              style={{
                flex: 1, background: tab === t ? "#161b22" : "none",
                border: tab === t ? "1px solid #30363d" : "1px solid transparent",
                borderRadius: 8, color: tab === t ? "#e6edf3" : "#8b949e",
                padding: "8px 0", fontSize: 13, fontWeight: tab === t ? 700 : 500,
                cursor: "pointer", transition: "all .2s",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* 성공 메시지 */}
        {successMsg && (
          <div style={{ background: "rgba(63,185,80,.15)", border: "1px solid #3fb950", borderRadius: 8, padding: "10px 14px", color: "#3fb950", fontSize: 13, marginBottom: 16, fontWeight: 600 }}>
            ✅ {successMsg}
          </div>
        )}

        {/* 오류 메시지 */}
        {error && (
          <div style={{ background: "rgba(248,81,73,.12)", border: "1px solid #f85149", borderRadius: 8, padding: "10px 14px", color: "#f85149", fontSize: 13, marginBottom: 16 }}>
            ⚠️ {error}
          </div>
        )}

        {/* 로그인 폼 */}
        {tab === "login" && (
          <form onSubmit={handleLoginSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>아이디</label>
              <input
                style={inputStyle} type="text" placeholder="아이디 입력"
                value={form.username} onChange={set("username")} autoFocus required
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>비밀번호</label>
              <input
                style={inputStyle} type="password" placeholder="비밀번호 입력"
                value={form.password} onChange={set("password")} required
              />
            </div>
            <button type="submit" style={btnPrimary} disabled={loading}>
              {loading ? "🔄 로그인 중..." : "로그인"}
            </button>
            <div style={{ textAlign: "center", marginTop: 14, color: "#8b949e", fontSize: 12 }}>
              계정이 없으신가요?{" "}
              <span
                onClick={() => { setTab("register"); setError(""); }}
                style={{ color: "#ff6b35", cursor: "pointer", fontWeight: 600 }}
              >
                회원가입
              </span>
            </div>
          </form>
        )}

        {/* 회원가입 폼 */}
        {tab === "register" && (
          <form onSubmit={handleRegisterSubmit}>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>이메일</label>
              <input
                style={inputStyle} type="email" placeholder="example@email.com"
                value={form.email} onChange={set("email")} autoFocus required
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>아이디 (3자 이상)</label>
              <input
                style={inputStyle} type="text" placeholder="사용할 아이디 입력"
                value={form.username} onChange={set("username")} required
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>비밀번호 (6자 이상)</label>
              <input
                style={inputStyle} type="password" placeholder="비밀번호 입력"
                value={form.password} onChange={set("password")} required
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>비밀번호 확인</label>
              <input
                style={inputStyle} type="password" placeholder="비밀번호 재입력"
                value={form.confirm} onChange={set("confirm")} required
              />
            </div>
            <button type="submit" style={btnPrimary} disabled={loading}>
              {loading ? "🔄 처리 중..." : "회원가입"}
            </button>
            <div style={{ textAlign: "center", marginTop: 14, color: "#8b949e", fontSize: 12 }}>
              이미 계정이 있으신가요?{" "}
              <span
                onClick={() => { setTab("login"); setError(""); }}
                style={{ color: "#ff6b35", cursor: "pointer", fontWeight: 600 }}
              >
                로그인
              </span>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  헤더
// ══════════════════════════════════════════════════════════════

function Header({ balance, inventoryValue, loggedIn, steamUser, onLogin, onOpenDeposit, loginLoading, onGoHome, onResetDb, onOpenInventory, onGoProfile }) {
  return (
    <header style={{
      background: "linear-gradient(90deg,#0d1117,#161b22)",
      borderBottom: "1px solid #30363d",
      padding: "0 24px", height: 64,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      position: "sticky", top: 0, zIndex: 100,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
        <div
          onClick={onGoHome}
          style={{ fontWeight: 800, fontSize: 22, background: "linear-gradient(90deg,#ff6b35,#f7c59f)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", cursor: "pointer", userSelect: "none" }}
        >
          🚗 MotorcarCase
        </div>
        <nav style={{ display: "flex", gap: 20 }}>
          <span style={{ color: "#e6edf3", fontSize: 14, cursor: "pointer", fontWeight: 600 }}>케이스</span>
        </nav>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {loggedIn && (
          <>
            <div style={{ background: "#1c2128", border: "1px solid #30363d", borderRadius: 8, padding: "6px 14px", display: "flex", alignItems: "center", gap: 10 }}>
              {/* 잔액 + 인벤토리 가치 세로 표시 */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ color: "#8b949e", fontSize: 10 }}>잔액</span>
                  <span style={{ color: "#3fb950", fontWeight: 700, fontSize: 15 }}>${balance.toFixed(2)}</span>
                </div>
                <div
                  onClick={onOpenInventory}
                  style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}
                  title="인벤토리 보기"
                >
                  <span style={{ color: "#8b949e", fontSize: 10 }}>인벤토리</span>
                  <span style={{ color: "#8847ff", fontWeight: 600, fontSize: 12, textDecoration: "underline dotted" }}>${inventoryValue.toFixed(2)}</span>
                </div>
              </div>
              <div style={{ width: 1, height: 28, background: "#30363d" }} />
              <button onClick={() => onOpenDeposit("deposit")} style={{ background: "linear-gradient(90deg,#238636,#2ea043)", border: "none", borderRadius: 6, color: "#fff", padding: "4px 12px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>+ 입금</button>
              <button onClick={() => onOpenDeposit("withdraw")} style={{ background: "none", border: "1px solid #30363d", borderRadius: 6, color: "#8b949e", padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>출금</button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg,#ff6b35,#f7c59f)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, cursor: "pointer" }} onClick={onGoProfile}>👤</div>
              <div onClick={onGoProfile} style={{ cursor: "pointer" }} title="프로필 보기">
                <div style={{ color: "#e6edf3", fontSize: 13, fontWeight: 600, textDecoration: "underline dotted", textUnderlineOffset: 3 }}>{steamUser?.personaname || "User"}</div>
                <div style={{ color: "#8b949e", fontSize: 10 }}>🟢 로그인됨</div>
              </div>
            </div>
          </>
        )}
        {!loggedIn && (
          <button onClick={onLogin} style={{ background: "linear-gradient(90deg,#ff6b35,#f7931e)", border: "none", borderRadius: 8, color: "#fff", padding: "8px 20px", fontSize: 14, cursor: "pointer", fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
            🔑 로그인 / 회원가입
          </button>
        )}
      </div>
    </header>
  );
}

// ══════════════════════════════════════════════════════════════
//  프로필 페이지
// ══════════════════════════════════════════════════════════════

function ProfilePage({ steamUser, balance, inventory, transactions, onBack, onLogout }) {
  const inventoryValue = inventory.reduce((s, i) => s + i.price, 0);
  const totalSpent = transactions
    .filter((t) => t.type === "case-open")
    .reduce((s, t) => s + Math.abs(t.amount), 0);
  const totalWon = transactions
    .filter((t) => t.type === "case-open")
    .reduce((s, t) => s + (t.itemPrice || 0), 0);

  return (
    <div style={{ maxWidth: 760, margin: "40px auto", padding: "0 24px", animation: "fadeIn .3s ease" }}>
      {/* 상단 버튼 행 */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <button
          onClick={onBack}
          style={{ background: "none", border: "1px solid #30363d", borderRadius: 8, color: "#8b949e", padding: "7px 16px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
        >
          ← 홈으로
        </button>
        <button
          onClick={onLogout}
          style={{ background: "none", border: "1px solid #f85149", borderRadius: 8, color: "#f85149", padding: "7px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, transition: "all .2s" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(248,81,73,.12)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
        >
          🚪 로그아웃
        </button>
      </div>

      {/* 프로필 카드 */}
      <div style={{ background: "linear-gradient(135deg,#161b22,#1c2128)", border: "1px solid #30363d", borderRadius: 16, padding: "32px 36px", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 28 }}>
          <div style={{ width: 72, height: 72, borderRadius: "50%", background: "linear-gradient(135deg,#ff6b35,#f7c59f)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, flexShrink: 0 }}>👤</div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#e6edf3", marginBottom: 4 }}>{steamUser?.personaname || "User"}</div>
            <div style={{ fontSize: 12, color: "#8b949e", marginBottom: 4 }}>🟢 로그인됨</div>
            {steamUser?.email && (
              <div style={{ fontSize: 12, color: "#8b949e" }}>✉️ {steamUser.email}</div>
            )}
          </div>
        </div>

        {/* 통계 4칸 */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 14 }}>
          {[
            { label: "💰 잔액",        value: `$${balance.toFixed(2)}`,           color: "#3fb950" },
            { label: "🎒 인벤토리",    value: `$${inventoryValue.toFixed(2)}`,     color: "#8847ff" },
            { label: "📦 케이스 오픈", value: `${transactions.filter((t) => t.type === "case-open").length}회`, color: "#ff6b35" },
            { label: "🎁 보유 아이템", value: `${inventory.length}개`,             color: "#f0a500" },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ background: "#0d1117", border: "1px solid #30363d", borderRadius: 10, padding: "14px 18px" }}>
              <div style={{ color: "#8b949e", fontSize: 11, marginBottom: 6 }}>{label}</div>
              <div style={{ color, fontWeight: 800, fontSize: 20 }}>{value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 최근 거래 내역 */}
      <div style={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 16, padding: "24px 28px" }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: "#e6edf3" }}>📋 최근 거래 내역</h3>
        {transactions.length === 0 ? (
          <div style={{ color: "#8b949e", fontSize: 14, textAlign: "center", padding: "24px 0" }}>아직 거래 내역이 없어요</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {transactions.slice(0, 15).map((t, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#0d1117", borderRadius: 8, border: "1px solid #21262d" }}>
                <div>
                  <div style={{ fontSize: 13, color: "#e6edf3", fontWeight: 600 }}>{t.label || t.type}</div>
                  <div style={{ fontSize: 11, color: "#8b949e", marginTop: 2 }}>{t.date ? new Date(t.date).toLocaleString("ko-KR") : ""}</div>
                </div>
                <div style={{ fontWeight: 700, fontSize: 14, color: t.amount >= 0 ? "#3fb950" : "#f85149" }}>
                  {t.amount >= 0 ? "+" : ""}${t.amount?.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  케이스 카드
// ══════════════════════════════════════════════════════════════

function CaseCard({ c, onSelect, imageMap, priceLoadStatus }) {
  const [hovered, setHovered] = useState(false);
  const topItem = c.items.find((i) => i.rarity === "covert") || c.items[0];
  const priceLabel = priceLoadStatus === "done"
    ? `$${c.price.toFixed(2)}`
    : priceLoadStatus === "error"
    ? "가격 오류"
    : "…";
  const evLabel = priceLoadStatus === "done"
    ? `기대값 $${(c.price * (1 - c.edge / 100)).toFixed(2)}`
    : priceLoadStatus === "error"
    ? "기대값 —"
    : "기대값 …";

  return (
    <div onClick={() => onSelect(c)}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? "#1c2128" : "#161b22",
        border: `1px solid ${hovered ? c.color : "#30363d"}`,
        borderRadius: 16, padding: 20, cursor: "pointer",
        transition: "all .25s", transform: hovered ? "translateY(-4px)" : "none",
        boxShadow: hovered ? `0 8px 32px ${c.color}33` : "none",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
      }}>
      {/* 케이스 대표 이미지 - 최고등급 아이템 미리보기 */}
      <div style={{ width: 120, height: 80, display: "flex", alignItems: "center", justifyContent: "center", filter: hovered ? `drop-shadow(0 0 12px ${c.color})` : "none", transition: "filter .25s" }}>
        <SkinImage item={topItem} imageMap={imageMap} size={110} />
      </div>
      <div style={{ fontWeight: 700, fontSize: 15, color: "#e6edf3" }}>{c.name}</div>
      <div style={{
        background: priceLoadStatus === "error" ? "none" : "linear-gradient(90deg,#ff6b35,#f7c59f)",
        WebkitBackgroundClip: priceLoadStatus === "error" ? "unset" : "text",
        WebkitTextFillColor: priceLoadStatus === "error" ? "#f85149" : "transparent",
        color: priceLoadStatus === "error" ? "#f85149" : undefined,
        fontWeight: 800, fontSize: 18,
      }}>{priceLabel}</div>
      {/* 이득률 배지 */}
      {c.edge != null && (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{
            background: c.edge <= 10 ? "#1a4a2e" : c.edge <= 20 ? "#1a3a4a" : c.edge <= 30 ? "#3a1a4a" : "#4a1a1a",
            border: `1px solid ${c.edge <= 10 ? "#3fb950" : c.edge <= 20 ? "#4b69ff" : c.edge <= 30 ? "#8847ff" : "#eb4b4b"}`,
            color: c.edge <= 10 ? "#3fb950" : c.edge <= 20 ? "#79a4ff" : c.edge <= 30 ? "#c98fff" : "#ff7b7b",
            borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
          }}>이득률 {c.edge}%</span>
          <span style={{ fontSize: 10, color: priceLoadStatus === "error" ? "#f85149" : "#8b949e" }}>{evLabel}</span>
        </div>
      )}
      {c.type === "sticker" && (
        <span style={{ fontSize: 10, fontWeight: 700, color: "#ff7043", background: "#ff704322", border: "1px solid #ff704366", borderRadius: 4, padding: "2px 7px", letterSpacing: 0.5 }}>🎭 STICKER CAPSULE</span>
      )}
      <button style={{ width: "100%", background: `linear-gradient(90deg,${c.color}99,${c.color})`, border: "none", borderRadius: 8, color: "#fff", padding: "9px 0", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
        {c.type === "sticker" ? "캡슐 열기" : "케이스 열기"}
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  룰렛 릴 (이미지 버전)
// ══════════════════════════════════════════════════════════════

function RouletteReel({ items, spinning, winner, imageMap, casePrice, vertical = false, priceLoadStatus }) {
  // ── 레이아웃 상수 ──────────────────────────────────────────
  const ITEM_W    = vertical ? 300 : 128;
  const ITEM_H    = vertical ? 84  : 108;
  const ITEM_GAP  = 8;
  const VISIBLE   = vertical ? 5   : 7;
  const TOTAL     = vertical ? 50  : 85;
  const CENTER_IDX = vertical ? 20 : 42;
  const STEP      = (vertical ? ITEM_H : ITEM_W) + ITEM_GAP;
  const visC      = Math.floor(VISIBLE / 2); // 화면 중앙 인덱스 (0-based)

  // 수직 모드: 릴 끝부분에서 시작해 위쪽(낮은 인덱스)으로 스크롤 → 위→아래로 낙하하는 시각 효과
  const initOffset = vertical ? -(6 + (TOTAL - VISIBLE) * STEP) : 0;

  const [reel] = useState(() =>
    Array.from({ length: TOTAL }, () => items[Math.floor(Math.random() * items.length)])
  );

  const displayReel = [...reel];
  if (winner) displayReel[CENTER_IDX] = winner;

  const [offset, setOffset] = useState(initOffset);
  const [done, setDone]     = useState(false);

  // winner가 세팅될 때 애니메이션 시작
  // double-rAF: 브라우저가 initOffset을 실제로 페인트한 뒤에 target으로 변경해야
  // CSS transition이 확실히 발동됨 (React 배칭으로 인한 전환 누락 방지)
  useEffect(() => {
    if (!winner || !spinning) return;
    setDone(false);

    const jitter = vertical
      ? Math.random() * ITEM_H * 0.3 - ITEM_H * 0.15
      : Math.random() * ITEM_W * 0.6 - ITEM_W * 0.3;
    const target = -(CENTER_IDX - visC) * STEP + jitter;

    let raf1, raf2, t2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setOffset(target); // 브라우저가 initOffset 위치를 페인트한 직후 → CSS transition 4.3s 발동
        t2 = setTimeout(() => setDone(true), 4300);
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(t2);
    };
  }, [winner]);

  // ── 공통 아이템 카드 렌더 ──────────────────────────────────
  const renderCard = (item, i) => {
    const isWin    = done && i === CENTER_IDX;
    const car      = isCarItem(item, casePrice);
    const bColor   = car ? "#ffd700" : item.color;
    return (
      <div key={i} style={{
        width: ITEM_W, height: ITEM_H, flexShrink: 0,
        borderRadius: 10,
        background: isWin ? (car ? "#ffd70022" : `${item.color}22`) : "#161b22",
        border: `2px solid ${isWin ? bColor : bColor + "55"}`,
        display: "flex",
        flexDirection: vertical ? "row" : "column",
        alignItems: "center",
        justifyContent: vertical ? "flex-start" : "center",
        gap: 0,
        padding: vertical ? "0 14px" : 0,
        boxShadow: isWin ? `0 0 24px ${bColor}99, inset 0 0 20px ${bColor}22` : "none",
        transition: "box-shadow .4s, background .4s",
        overflow: "hidden",
      }}>
        {vertical ? (
          // 수직 모드: 가로 레이아웃 (이미지 + 이름/가격)
          <>
            <div style={{ width: 64, height: "100%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <SkinImage item={item} imageMap={imageMap} size={56} isCar={car} />
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingLeft: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: car ? "#ffd700" : "#e6edf3", lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.name.split(" | ")[0]}
              </div>
              <div style={{ fontSize: 10, color: bColor, opacity: 0.8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.name.split(" | ")[1] || ""}
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: priceLoadStatus === "error" ? "#f85149" : car ? "#ffd700" : "#3fb950", marginTop: 3 }}>
                {priceLoadStatus === "done" ? `$${item.price.toFixed(2)}` : priceLoadStatus === "error" ? "오류" : "…"}
              </div>
            </div>
            <div style={{ width: 3, height: "60%", background: bColor, borderRadius: 2, opacity: 0.8, flexShrink: 0 }} />
          </>
        ) : (
          // 수평 모드: 기존 세로 레이아웃 (이미지 위, 컬러바 아래)
          <>
            <div style={{ width: "100%", flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 8px" }}>
              <SkinImage item={item} imageMap={imageMap} size={88} isCar={car} />
            </div>
            <div style={{ width: "80%", height: 3, background: bColor, borderRadius: 1, opacity: 0.9, marginBottom: 6 }} />
          </>
        )}
      </div>
    );
  };

  // ── 수평 룰렛 ──────────────────────────────────────────────
  if (!vertical) {
    return (
      <div style={{ position: "relative", width: VISIBLE * (ITEM_W + ITEM_GAP) - ITEM_GAP, overflow: "hidden", margin: "0 auto", borderRadius: 12, border: "1px solid #30363d" }}>
        {/* 중앙 수직 마커 */}
        <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 3, background: "linear-gradient(180deg,#ff6b35,#f7c59f)", transform: "translateX(-50%)", zIndex: 10, pointerEvents: "none", boxShadow: "0 0 8px #ff6b35" }} />
        {/* 좌우 그라디언트 */}
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 100, background: "linear-gradient(90deg,#0d1117,transparent)", zIndex: 5, pointerEvents: "none" }} />
        <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 100, background: "linear-gradient(270deg,#0d1117,transparent)", zIndex: 5, pointerEvents: "none" }} />
        <div style={{
          display: "flex", flexDirection: "row", gap: ITEM_GAP,
          transition: spinning ? "transform 4.3s cubic-bezier(0.12,0.8,0.25,1)" : "none",
          transform: `translateX(${offset}px)`,
          willChange: "transform", padding: "6px 0",
        }}>
          {displayReel.map(renderCard)}
        </div>
      </div>
    );
  }

  // ── 수직 룰렛 (잭팟 재룰렛) ────────────────────────────────
  const containerH = VISIBLE * (ITEM_H + ITEM_GAP) - ITEM_GAP;
  return (
    <div style={{ position: "relative", height: containerH, width: ITEM_W, overflow: "hidden", margin: "0 auto", borderRadius: 14, border: "2px solid #ffd70055", boxShadow: "0 0 40px #ffd70033" }}>
      {/* 중앙 수평 마커 */}
      <div style={{ position: "absolute", left: 0, right: 0, top: "50%", height: 3, background: "linear-gradient(90deg,transparent,#ffd700,#ff6b35,#ffd700,transparent)", transform: "translateY(-50%)", zIndex: 10, pointerEvents: "none", boxShadow: "0 0 12px #ffd700" }} />
      {/* 상하 그라디언트 */}
      <div style={{ position: "absolute", left: 0, right: 0, top: 0, height: 90, background: "linear-gradient(180deg,#0d1117ee,transparent)", zIndex: 5, pointerEvents: "none" }} />
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 90, background: "linear-gradient(0deg,#0d1117ee,transparent)", zIndex: 5, pointerEvents: "none" }} />
      <div style={{
        display: "flex", flexDirection: "column", gap: ITEM_GAP,
        transition: spinning ? "transform 4.3s cubic-bezier(0.12,0.8,0.25,1)" : "none",
        transform: `translateY(${offset}px)`,
        willChange: "transform", padding: "6px 0",
      }}>
        {displayReel.map(renderCard)}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  드롭 테이블 — CasePage 밖에 독립 컴포넌트로 정의
//  (안에 넣으면 매 렌더마다 새 컴포넌트 취급 → SkinImage 상태 리셋)
// ══════════════════════════════════════════════════════════════

function DropTable({ c, imageMap, priceLoadStatus }) {
  return (
    <div>
      <div style={{ color: "#8b949e", fontSize: 12, marginBottom: 12, fontWeight: 600, letterSpacing: 0.5 }}>포함 아이템 · 드롭 확률</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
        {[...c.items].sort((a, b) => b.price - a.price).map((item, i) => {
          const car = isCarItem(item, c.price);
          const parts = item.name.split(" | ");
          const gun = parts[0] || "";
          const skin = parts[1] || "";
          const skinNoWear = skin.replace(/\s*\([^)]*\)\s*$/, "").trim();
          const wearMatch  = skin.match(/\(([^)]*)\)/);
          const wear       = wearMatch ? wearMatch[1] : "";
          return (
            <div key={i} style={{ background: "#1c2128", border: `1px solid ${car ? "#ffd700" : item.color + "55"}`, borderRadius: 12, padding: "12px 10px 10px", display: "flex", flexDirection: "column", alignItems: "center", boxShadow: car ? "0 0 10px rgba(255,215,0,.2)" : "none" }}>
              <div style={{ width: "100%", height: 90, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
                <SkinImage item={item} imageMap={imageMap} size={80} isCar={false} />
              </div>
              <div style={{ width: "100%", height: 2, background: car ? "#ffd700" : item.color, borderRadius: 1, opacity: 0.85, marginBottom: 8 }} />
              <div style={{ width: "100%", textAlign: "center", lineHeight: 1.4 }}>
                <div style={{ fontSize: 10, color: "#8b949e" }}>{gun}</div>
                <div style={{ fontSize: 11, color: car ? "#ffd700" : "#e6edf3", fontWeight: 700 }}>{skinNoWear}</div>
                {wear && <div style={{ fontSize: 9, color: car ? "#ffd700" : item.color, opacity: 0.8 }}>({wear})</div>}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", marginTop: 8, paddingTop: 6, borderTop: "1px solid #30363d" }}>
                <span style={{ fontSize: 10, color: "#8b949e" }}>{item.chance.toFixed(2)}%</span>
                <span style={{ fontSize: 11, color: priceLoadStatus === "error" ? "#f85149" : car ? "#ffd700" : "#3fb950", fontWeight: 700 }}>
                  {priceLoadStatus === "done" ? `$${item.price.toFixed(2)}` : priceLoadStatus === "error" ? "오류" : "…"}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  케이스 상세 페이지 (카테고리 → 하위 카테고리)
// ══════════════════════════════════════════════════════════════

function CasePage({ c, onBack, balance, onUpdateBalance, onAddInventory, onAddTx, onSellItem, imageMap, priceLoadStatus }) {
  // phase: "idle" | "spinning" | "jackpot-wait" | "result"
  const [phase,        setPhase]        = useState("idle");
  const [winner,       setWinner]       = useState(null);
  const [result,       setResult]       = useState(null);
  const [reelKey,      setReelKey]      = useState(0);
  const [isRespinning, setIsRespinning] = useState(false);

  const canAfford = balance >= c.price;
  const carItems  = c.items.filter((i) => isCarItem(i, c.price));

  // imageMap이 준비되면 케이스 아이템 이미지 전부 미리 캐시
  useEffect(() => {
    if (!imageMap) return;
    c.items.forEach((item) => {
      const url = imageMap[item.name];
      if (url) { const img = new Image(); img.src = url; }
    });
  }, [imageMap, c.items]);

  const doSpin = (itemPool, isRespin = false) => {
    setIsRespinning(isRespin);
    setReelKey((k) => k + 1);
    setPhase("spinning");
    setWinner(null);
    const won = weightedRandom(itemPool);
    setTimeout(() => setWinner(won), 30);

    setTimeout(() => {
      const wonIsCar = isCarItem(won, c.price);
      if (wonIsCar && !isRespin) {
        setResult(won);
        setPhase("jackpot-wait");
        setTimeout(() => {
          setResult(null);
          doSpin(carItems, true);
        }, 1500);
      } else {
        setResult(won);
        setPhase("result");
        onAddInventory(won);
      }
    }, 4800);
  };

  const openCase = () => {
    if (!canAfford || phase === "spinning") return;
    onUpdateBalance(-c.price);
    onAddTx({ type: "case-open", amount: -c.price, label: `${c.name} 오픈`, date: new Date().toISOString() });
    doSpin(c.items, false);
  };

  const handleBackToCase = () => {
    setPhase("idle");
    setResult(null);
    setWinner(null);
    setReelKey((k) => k + 1);
    setIsRespinning(false);
  };

  const handleSell = () => {
    if (!result) return;
    onSellItem(result);
    handleBackToCase();
  };


  // ══════════════════════════════════════════════════════════
  //  PHASE: result — 결과 화면
  // ══════════════════════════════════════════════════════════
  if (phase === "result" && result) {
    const car      = isCarItem(result, c.price);
    const txtColor = car ? "#ffd700" : result.color;
    return (
      <div style={{ maxWidth: 1000, margin: "0 auto", animation: "fadeIn .4s ease" }}>
        {/* 브레드크럼 */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
          <button onClick={handleBackToCase} style={{ background: "#1c2128", border: "1px solid #30363d", borderRadius: 8, color: "#8b949e", padding: "6px 14px", fontSize: 13, cursor: "pointer", fontWeight: 600 }}>← 뒤로</button>
          <span style={{ color: "#8b949e", fontSize: 13, cursor: "pointer" }} onClick={onBack}>🎁 케이스</span>
          <span style={{ color: "#484f58", fontSize: 13 }}>›</span>
          <span style={{ color: "#e6edf3", fontWeight: 700, fontSize: 13 }}>{c.image} {c.name}</span>
        </div>

        {/* 결과 카드 */}
        <div style={{ background: "#161b22", border: `1px solid ${car ? "#ffd700" : result.color + "55"}`, borderRadius: 20, padding: "48px 32px", textAlign: "center", boxShadow: `0 0 40px ${car ? "#ffd70022" : result.color + "22"}` }}>

          <div style={{ fontSize: 13, color: "#8b949e", marginBottom: 12, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>
            {car ? "🏆 JACKPOT 획득!" : "🎉 획득 아이템"}
          </div>

          {/* 아이템 이미지 */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
            <div style={{ padding: 24, background: car ? "#ffd70011" : `${result.color}11`, border: `2px solid ${car ? "#ffd700" : result.color}44`, borderRadius: 20 }}>
              <SkinImage item={result} imageMap={imageMap} size={140} isCar={car} />
            </div>
          </div>

          {/* 아이템 정보 */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: txtColor, marginBottom: 6 }}>{result.name}</div>
            <div style={{ fontSize: 13, color: txtColor, opacity: 0.7, marginBottom: 10 }}>{RARITY_LABEL[result.rarity]}</div>
            <div style={{ fontSize: 32, fontWeight: 900, color: car ? "#ffd700" : "#3fb950" }}>${result.price.toFixed(2)}</div>
            {car && <div style={{ fontSize: 12, color: "#ffd700", marginTop: 6 }}>🚗 케이스 가격의 {(result.price / c.price).toFixed(0)}배 당첨!</div>}
          </div>

          {/* 버튼 2개 */}
          <div style={{ display: "flex", justifyContent: "center", gap: 14 }}>
            <button
              onClick={handleBackToCase}
              style={{ background: "none", border: "1px solid #30363d", borderRadius: 10, color: "#e6edf3", padding: "13px 28px", fontSize: 15, fontWeight: 700, cursor: "pointer", transition: "all .2s" }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#1c2128"}
              onMouseLeave={(e) => e.currentTarget.style.background = "none"}
            >
              📦 케이스로 돌아가기
            </button>
            <button
              onClick={handleSell}
              style={{ background: "linear-gradient(90deg,#238636,#2ea043)", border: "none", borderRadius: 10, color: "#fff", padding: "13px 28px", fontSize: 15, fontWeight: 700, cursor: "pointer", transition: "all .2s" }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
              onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
            >
              💰 팔기 (+${result.price.toFixed(2)})
            </button>
          </div>
        </div>

        {/* 드롭 테이블 */}
        <div style={{ marginTop: 24 }}>
          <DropTable c={c} imageMap={imageMap} priceLoadStatus={priceLoadStatus} />
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  //  PHASE: idle / spinning / jackpot-wait
  // ══════════════════════════════════════════════════════════
  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      {/* 브레드크럼 */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: "#1c2128", border: "1px solid #30363d", borderRadius: 8, color: "#8b949e", padding: "6px 14px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontWeight: 600 }}>← 뒤로</button>
        <span style={{ color: "#8b949e", fontSize: 13, cursor: "pointer" }} onClick={onBack}>🎁 케이스</span>
        <span style={{ color: "#484f58", fontSize: 13 }}>›</span>
        <span style={{ color: "#e6edf3", fontWeight: 700, fontSize: 13 }}>{c.image} {c.name}</span>
      </div>

      <div style={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 20, padding: "28px 32px 32px" }}>

        {/* 타이틀 */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 42 }}>{c.image}</div>
          <h2 style={{ color: "#e6edf3", margin: "6px 0 2px", fontWeight: 800 }}>{c.name}</h2>
          <div style={{ color: "#8b949e", fontSize: 13 }}>${c.price.toFixed(2)} / 1회 오픈</div>
        </div>

        {/* 룰렛 — spinning / jackpot-wait 일 때만 표시 */}
        {(phase === "spinning" || phase === "jackpot-wait") && (
          <div style={{ marginBottom: 20, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, animation: "fadeIn .3s ease" }}>
            {isRespinning && (
              <div style={{ fontSize: 13, fontWeight: 700, color: "#ffd700", letterSpacing: 1 }}>
                🚗 프리미엄 아이템 재룰렛 — 위에서 아래로!
              </div>
            )}
            <RouletteReel
              key={reelKey}
              items={isRespinning ? carItems : c.items}
              spinning={phase === "spinning"}
              winner={winner}
              imageMap={imageMap}
              casePrice={c.price}
              vertical={isRespinning}
              priceLoadStatus={priceLoadStatus}
            />
          </div>
        )}

        {/* 잭팟 대기 메시지 */}
        {phase === "jackpot-wait" && (
          <div style={{ textAlign: "center", marginBottom: 18, animation: "fadeIn .4s ease" }}>
            <div style={{ fontSize: 48, marginBottom: 8, animation: "spin 0.6s linear infinite" }}>🚗</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#ffd700", marginBottom: 4 }}>🚗 JACKPOT! 프리미엄 재룰렛!</div>
            <div style={{ fontSize: 13, color: "#8b949e" }}>케이스 가격의 6배 이상 아이템만으로 다시 도전!</div>
          </div>
        )}

        {/* 열기 버튼 — idle 일 때만 */}
        {phase === "idle" && (
          <>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
              <button
                onClick={openCase}
                disabled={!canAfford}
                style={{
                  background: canAfford ? "linear-gradient(90deg,#ff6b35,#f7c59f)" : "#30363d",
                  border: "none", borderRadius: 10,
                  color: canAfford ? "#0d1117" : "#8b949e",
                  padding: "14px 52px", fontWeight: 800, fontSize: 17,
                  cursor: canAfford ? "pointer" : "not-allowed", transition: "all .2s",
                  boxShadow: canAfford ? "0 4px 20px #ff6b3544" : "none",
                }}
              >
                {c.type === "sticker" ? "🎭 캡슐 열기" : "🎰 케이스 열기"} (${c.price.toFixed(2)})
              </button>
            </div>
            {!canAfford && (
              <div style={{ textAlign: "center", color: "#f85149", fontSize: 13, marginBottom: 16 }}>
                잔액이 부족합니다. 입금 후 이용해 주세요.
              </div>
            )}
          </>
        )}

        {/* 드롭 테이블 — 항상 표시 */}
        <div style={{ marginTop: phase !== "idle" ? 24 : 0 }}>
          <DropTable c={c} imageMap={imageMap} priceLoadStatus={priceLoadStatus} />
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  인벤토리 패널 (이미지 버전)
// ══════════════════════════════════════════════════════════════

function InventoryPanel({ items, onSell, imageMap }) {
  if (items.length === 0)
    return (
      <div style={{ textAlign: "center", color: "#8b949e", padding: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
        <div>인벤토리가 비어 있습니다</div>
        <div style={{ fontSize: 12, marginTop: 4 }}>케이스를 열어 아이템을 획득하세요!</div>
      </div>
    );
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12 }}>
      {items.map((item, i) => (
        <div key={i} style={{ background: "#1c2128", border: `1px solid ${item.color}44`, borderRadius: 12, padding: 12, textAlign: "center" }}>
          <div style={{ height: 72, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 6 }}>
            <SkinImage item={item} imageMap={imageMap} size={80} />
          </div>
          <div style={{ width: "100%", height: 2, background: item.color, borderRadius: 1, marginBottom: 6, opacity: 0.8 }} />
          <div style={{ fontSize: 11, color: "#e6edf3", fontWeight: 600, marginBottom: 1 }}>{item.name.split("|")[0].trim()}</div>
          <div style={{ fontSize: 10, color: item.color, marginBottom: 6 }}>{item.name.split("|")[1]?.trim()}</div>
          <div style={{ fontSize: 14, color: "#3fb950", fontWeight: 700, marginBottom: 8 }}>${item.price.toFixed(2)}</div>
          <button onClick={() => onSell(i, item)} style={{ width: "100%", background: "none", border: "1px solid #30363d", borderRadius: 6, color: "#8b949e", padding: "5px 0", fontSize: 11, cursor: "pointer" }}>판매</button>
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  입출금 모달
// ══════════════════════════════════════════════════════════════

function DepositWithdrawModal({ mode: initialMode, balance, onClose, onDeposit, onWithdraw, transactions }) {
  const [mode, setMode] = useState(initialMode || "deposit");
  const [amount, setAmount] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [payMethod, setPayMethod] = useState("toss");
  const [withdrawAddr, setWithdrawAddr] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const finalAmount = parseFloat(customAmount || amount || 0);

  const handleDeposit = async () => {
    if (!finalAmount || finalAmount < 1) return;
    setLoading(true); setStep(2);
    await delay(1800);
    onDeposit(finalAmount); setStep(3); setLoading(false);
  };
  const handleWithdraw = async () => {
    if (!finalAmount || finalAmount < 5 || finalAmount > balance || !withdrawAddr) return;
    setLoading(true); setStep(2);
    await delay(1500);
    onWithdraw(finalAmount); setStep(3); setLoading(false);
  };
  const reset = () => { setAmount(""); setCustomAmount(""); setStep(1); setWithdrawAddr(""); };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.85)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 20, width: "min(480px,95vw)", padding: 32, position: "relative", maxHeight: "90vh", overflowY: "auto" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", color: "#8b949e", fontSize: 22, cursor: "pointer" }}>✕</button>

        <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #30363d", marginBottom: 24 }}>
          {[["deposit", "💰 입금"], ["withdraw", "📤 출금"], ["history", "📋 내역"]].map(([id, label]) => (
            <button key={id} onClick={() => { setMode(id); reset(); }} style={{ background: "none", border: "none", borderBottom: `2px solid ${mode === id ? "#ff6b35" : "transparent"}`, color: mode === id ? "#ff6b35" : "#8b949e", padding: "10px 16px", cursor: "pointer", fontWeight: 600, fontSize: 14 }}>{label}</button>
          ))}
        </div>

        {/* 입금 */}
        {mode === "deposit" && step === 1 && (
          <>
            <div style={{ color: "#8b949e", fontSize: 13, marginBottom: 10 }}>금액 선택 (USD)</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 14 }}>
              {DEPOSIT_PRESETS.map((p) => (
                <button key={p} onClick={() => { setAmount(String(p)); setCustomAmount(""); }} style={{ background: amount === String(p) ? "#ff6b3522" : "#1c2128", border: `1px solid ${amount === String(p) ? "#ff6b35" : "#30363d"}`, borderRadius: 8, color: amount === String(p) ? "#ff6b35" : "#e6edf3", padding: "10px 0", cursor: "pointer", fontWeight: 700, fontSize: 15 }}>${p}</button>
              ))}
            </div>
            <input type="number" placeholder="직접 입력 (최소 $1)" value={customAmount} onChange={(e) => { setCustomAmount(e.target.value); setAmount(""); }} style={{ width: "100%", background: "#1c2128", border: "1px solid #30363d", borderRadius: 8, color: "#e6edf3", padding: "10px 14px", fontSize: 14, outline: "none", marginBottom: 16 }} />
            <div style={{ color: "#8b949e", fontSize: 13, marginBottom: 10 }}>결제 수단</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
              {PAYMENT_METHODS.map((pm) => (
                <button key={pm.id} onClick={() => setPayMethod(pm.id)} style={{ background: payMethod === pm.id ? "#1c2128" : "transparent", border: `1px solid ${payMethod === pm.id ? "#ff6b35" : "#30363d"}`, borderRadius: 8, color: "#e6edf3", padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 18 }}>{pm.icon}</span>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{pm.name}</span>
                  <span style={{ marginLeft: "auto", color: "#3fb950", fontSize: 12 }}>수수료 없음</span>
                  {payMethod === pm.id && <span style={{ color: "#ff6b35" }}>✓</span>}
                </button>
              ))}
            </div>
            {finalAmount > 0 && (
              <div style={{ background: "#1c2128", border: "1px solid #30363d", borderRadius: 8, padding: 14, marginBottom: 14 }}>
                {[["입금 금액", `$${finalAmount.toFixed(2)}`], ["수수료", "$0.00"], ["충전 후 잔액", `$${(balance + finalAmount).toFixed(2)}`]].map(([k, v], i) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", marginBottom: i < 2 ? 6 : 0, paddingTop: i === 2 ? 8 : 0, borderTop: i === 2 ? "1px solid #30363d" : "none" }}>
                    <span style={{ color: "#8b949e", fontSize: 13 }}>{k}</span>
                    <span style={{ color: i === 2 ? "#ff6b35" : "#e6edf3", fontWeight: i === 2 ? 700 : 400 }}>{v}</span>
                  </div>
                ))}
              </div>
            )}
            <button onClick={handleDeposit} disabled={!finalAmount || finalAmount < 1} style={{ width: "100%", background: finalAmount >= 1 ? "linear-gradient(90deg,#ff6b35,#f7c59f)" : "#30363d", border: "none", borderRadius: 10, color: finalAmount >= 1 ? "#0d1117" : "#8b949e", padding: "14px 0", fontWeight: 800, fontSize: 16, cursor: finalAmount >= 1 ? "pointer" : "not-allowed" }}>
              {finalAmount >= 1 ? `${PAYMENT_METHODS.find((p) => p.id === payMethod)?.icon} $${finalAmount.toFixed(2)} 입금하기` : "금액을 선택하세요"}
            </button>
          </>
        )}
        {mode === "deposit" && step === 2 && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
            <div style={{ color: "#e6edf3", fontWeight: 700, fontSize: 18 }}>결제 처리 중...</div>
          </div>
        )}
        {mode === "deposit" && step === 3 && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>✅</div>
            <div style={{ color: "#3fb950", fontWeight: 800, fontSize: 20 }}>입금 완료!</div>
            <div style={{ color: "#e6edf3", fontSize: 16, marginTop: 8 }}>${finalAmount.toFixed(2)} 충전됨</div>
            <button onClick={() => { reset(); onClose(); }} style={{ marginTop: 24, background: "linear-gradient(90deg,#ff6b35,#f7c59f)", border: "none", borderRadius: 10, color: "#0d1117", padding: "12px 32px", fontWeight: 700, cursor: "pointer" }}>확인</button>
          </div>
        )}

        {/* 출금 */}
        {mode === "withdraw" && step === 1 && (
          <>
            <div style={{ background: "#1c2128", border: "1px solid #d29922", borderRadius: 8, padding: 12, marginBottom: 18, display: "flex", gap: 10 }}>
              <span>⚠️</span>
              <div style={{ fontSize: 12, color: "#d29922" }}>Steam 계정 생성 30일 이상, CS2 플레이타임 10시간 이상 필요. 봇 거래 자동 차단.</div>
            </div>
            <div style={{ color: "#8b949e", fontSize: 13, marginBottom: 6 }}>출금 금액 (최소 $5)</div>
            <input type="number" placeholder="출금 금액" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} style={{ width: "100%", background: "#1c2128", border: "1px solid #30363d", borderRadius: 8, color: "#e6edf3", padding: "10px 14px", fontSize: 14, outline: "none", marginBottom: 14 }} />
            <div style={{ color: "#8b949e", fontSize: 13, marginBottom: 6 }}>Steam 트레이드 URL</div>
            <input placeholder="https://steamcommunity.com/tradeoffer/new/?partner=..." value={withdrawAddr} onChange={(e) => setWithdrawAddr(e.target.value)} style={{ width: "100%", background: "#1c2128", border: "1px solid #30363d", borderRadius: 8, color: "#e6edf3", padding: "10px 14px", fontSize: 12, outline: "none", marginBottom: 20 }} />
            <button onClick={handleWithdraw} disabled={!finalAmount || finalAmount < 5 || finalAmount > balance || !withdrawAddr} style={{ width: "100%", background: (finalAmount >= 5 && finalAmount <= balance && withdrawAddr) ? "linear-gradient(90deg,#1b4b8a,#1f6feb)" : "#30363d", border: "none", borderRadius: 10, color: (finalAmount >= 5 && finalAmount <= balance && withdrawAddr) ? "#fff" : "#8b949e", padding: "14px 0", fontWeight: 800, fontSize: 16, cursor: (finalAmount >= 5 && finalAmount <= balance && withdrawAddr) ? "pointer" : "not-allowed" }}>
              {!finalAmount || finalAmount < 5 ? "최소 $5 이상 입력" : finalAmount > balance ? "잔액 부족" : !withdrawAddr ? "트레이드 URL 입력 필요" : `$${finalAmount.toFixed(2)} 출금 신청`}
            </button>
          </>
        )}
        {mode === "withdraw" && step === 2 && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
            <div style={{ color: "#e6edf3", fontWeight: 700 }}>봇 탐지 시스템 확인 중...</div>
          </div>
        )}
        {mode === "withdraw" && step === 3 && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>📤</div>
            <div style={{ color: "#1f6feb", fontWeight: 800, fontSize: 20 }}>출금 신청 완료!</div>
            <div style={{ color: "#e6edf3", marginTop: 8 }}>${finalAmount.toFixed(2)} 처리 중</div>
            <button onClick={() => { reset(); onClose(); }} style={{ marginTop: 24, background: "linear-gradient(90deg,#1b4b8a,#1f6feb)", border: "none", borderRadius: 10, color: "#fff", padding: "12px 32px", fontWeight: 700, cursor: "pointer" }}>확인</button>
          </div>
        )}

        {/* 거래 내역 */}
        {mode === "history" && (
          <div>
            {transactions.length === 0 ? (
              <div style={{ textAlign: "center", color: "#8b949e", padding: "40px 0" }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
                <div>거래 내역이 없습니다</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {transactions.map((tx, i) => (
                  <div key={i} style={{ background: "#1c2128", border: "1px solid #30363d", borderRadius: 8, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 20 }}>{tx.type === "deposit" ? "💰" : tx.type === "withdraw" ? "📤" : "🎮"}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: "#e6edf3", fontWeight: 600 }}>{tx.type === "deposit" ? "입금" : tx.type === "withdraw" ? "출금" : "케이스 오픈"}</div>
                      <div style={{ fontSize: 11, color: "#8b949e" }}>{tx.time}</div>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: tx.amount > 0 ? "#3fb950" : "#f85149" }}>{tx.amount > 0 ? "+" : ""}${Math.abs(tx.amount).toFixed(2)}</div>
                    <div style={{ fontSize: 11, color: tx.status === "완료" ? "#3fb950" : "#d29922", border: `1px solid ${tx.status === "완료" ? "#3fb95044" : "#d2992244"}`, borderRadius: 4, padding: "2px 6px" }}>{tx.status}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  인벤토리 모달
// ══════════════════════════════════════════════════════════════


function InventoryModal({ inventory, onClose, onSell, onSellAll, imageMap }) {
  const total = inventory.reduce((s, i) => s + i.price, 0);
  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.85)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 20, width: "100%", maxWidth: 760, maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden", animation: "slideIn .2s ease" }}
      >
        {/* 헤더 */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: "1px solid #30363d" }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 3 }}>🎒 내 인벤토리</h2>
            <p style={{ color: "#8b949e", fontSize: 13 }}>
              아이템 {inventory.length}개 &nbsp;·&nbsp; 총 가치: <span style={{ color: "#3fb950", fontWeight: 700 }}>${total.toFixed(2)}</span>
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {inventory.length > 0 && (
              <button
                onClick={onSellAll}
                style={{ background: "none", border: "1px solid #f85149", borderRadius: 8, color: "#f85149", padding: "7px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
              >
                전체 판매
              </button>
            )}
            <button
              onClick={onClose}
              style={{ background: "none", border: "1px solid #30363d", borderRadius: 8, color: "#8b949e", padding: "7px 12px", cursor: "pointer", fontSize: 16, lineHeight: 1 }}
            >✕</button>
          </div>
        </div>

        {/* 내용 */}
        <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1 }}>
          {inventory.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#8b949e" }}>
              <div style={{ fontSize: 52, marginBottom: 12 }}>🎒</div>
              <div style={{ fontSize: 16 }}>인벤토리가 비어있습니다</div>
              <div style={{ fontSize: 13, marginTop: 6 }}>케이스를 열어 아이템을 획득하세요!</div>
            </div>
          ) : (
            <InventoryPanel items={inventory} onSell={onSell} imageMap={imageMap} />
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  업그레이드 페이지
// ══════════════════════════════════════════════════════════════

const UPGRADE_HOUSE_EDGE = 0.10;
const MULTIPLIERS = [
  { label: "1.5×", value: 1.5,  color: "#3fb950" },
  { label: "2×",   value: 2,    color: "#f0a500" },
  { label: "5×",   value: 5,    color: "#d32ce6" },
  { label: "10×",  value: 10,   color: "#eb4b4b" },
];

function UpgradePage({ inventory, cases, imageMap, onUpgradeWin, onUpgradeLose, loggedIn, onLoginRequired }) {
  const [selectedIdxs, setSelectedIdxs] = useState(new Set());
  const [targetItem,   setTargetItem]   = useState(null);
  const [spinning,     setSpinning]     = useState(false);
  const [result,       setResult]       = useState(null);
  const [resultItem,   setResultItem]   = useState(null); // 결과 화면에서 당첨 아이템 표시용
  const [needleDeg,    setNeedleDeg]    = useState(0);
  const [priceMin,     setPriceMin]     = useState("");
  const [priceMax,     setPriceMax]     = useState("");
  const currentDeg = useRef(0);

  // 선택된 아이템 배열 + 합산 가격
  const myItems      = [...selectedIdxs].map((i) => inventory[i]).filter(Boolean);
  const myTotalPrice = parseFloat(myItems.reduce((s, i) => s + i.price, 0).toFixed(2));
  const hasSelection = myItems.length > 0;

  const toggleItem = (idx) => {
    setSelectedIdxs((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
    setTargetItem(null);
    setResult(null);
  };

  // 전체 케이스 아이템, 가격 오름차순, 중복 제거
  const allCaseItems = useMemo(() => {
    const seen = new Set();
    const items = [];
    cases.forEach((c) => c.items.forEach((item) => {
      if (!seen.has(item.name)) { seen.add(item.name); items.push(item); }
    }));
    return items.sort((a, b) => a.price - b.price);
  }, [cases]);

  // 업그레이드 대상 아이템 이미지 프리로드 (결과 화면에서 이미지 깨짐 방지)
  useEffect(() => {
    if (!imageMap) return;
    allCaseItems.forEach((item) => {
      const url = imageMap[item.name];
      if (url) { const img = new Image(); img.src = url; }
    });
  }, [imageMap, allCaseItems]);

  // 가격 범위 필터 적용 (선택 아이템 합산의 1.1배 미만은 자동 제외)
  const filteredTargets = useMemo(() => {
    const min = parseFloat(priceMin) || 0;
    const max = parseFloat(priceMax) || Infinity;
    const autoMin = hasSelection ? myTotalPrice * 1.1 : 0;
    return allCaseItems.filter((i) => i.price >= Math.max(min, autoMin) && i.price <= max);
  }, [allCaseItems, priceMin, priceMax, hasSelection, myTotalPrice]);

  // 배율 자동선택: 합산 가격 × multiplier 에 가장 가까운 아이템
  const handleMultiplier = (mult) => {
    if (!hasSelection) return;
    const target = mult * myTotalPrice;
    const closest = allCaseItems.reduce((best, item) =>
      Math.abs(item.price - target) < Math.abs(best.price - target) ? item : best
    , allCaseItems[0]);
    setTargetItem(closest);
    setResult(null);
    setPriceMin("");
    setPriceMax("");
  };

  // 승률 (최대 90%)
  const winChance = useMemo(() => {
    if (!hasSelection || !targetItem) return 0;
    return Math.min(0.90, (myTotalPrice / targetItem.price) * (1 - UPGRADE_HOUSE_EDGE));
  }, [hasSelection, myTotalPrice, targetItem]);

  const winDeg = winChance * 360;
  const SIZE = 260; const R = 108; const cx = SIZE / 2; const cy = SIZE / 2;

  const arc = (startDeg, endDeg) => {
    const toRad = (d) => (d - 90) * (Math.PI / 180);
    const px = (d) => cx + R * Math.cos(toRad(d));
    const py = (d) => cy + R * Math.sin(toRad(d));
    const span = endDeg - startDeg;
    if (span <= 0) return "";
    if (span >= 360) return `M ${cx} ${cy - R} A ${R} ${R} 0 1 1 ${cx - 0.001} ${cy - R} Z`;
    const large = span > 180 ? 1 : 0;
    return `M ${cx} ${cy} L ${px(startDeg)} ${py(startDeg)} A ${R} ${R} 0 ${large} 1 ${px(endDeg)} ${py(endDeg)} Z`;
  };

  const handleSpin = () => {
    if (!hasSelection || !targetItem || spinning) return;
    if (!loggedIn) { onLoginRequired(); return; }
    setSpinning(true);
    setResult(null);
    const won = Math.random() < winChance;
    let landAngle;
    if (won) {
      landAngle = Math.random() * winDeg * 0.85 + winDeg * 0.05;
    } else {
      const loseStart = winDeg + 5;
      landAngle = Math.random() * (350 - loseStart) + loseStart;
    }
    const rotations = 4 + Math.floor(Math.random() * 3);
    const finalDeg = currentDeg.current + rotations * 360 + landAngle;
    currentDeg.current = finalDeg % 360;
    setNeedleDeg(finalDeg);
    setTimeout(() => {
      setSpinning(false);
      const wonItem = won ? targetItem : null;
      setResult(won ? "win" : "lose");
      setResultItem(wonItem);
      if (won) { onUpgradeWin(myItems, targetItem); }
      else     { onUpgradeLose(myItems); }
      setSelectedIdxs(new Set());
      setTargetItem(null);
      // 2.5초 후 전체 초기화
      setTimeout(() => {
        setResult(null);
        setResultItem(null);
        setPriceMin("");
        setPriceMax("");
      }, 2500);
    }, 3800);
  };

  const cardBase = (selected, color) => ({
    background: selected ? "#1c2128" : "#161b22",
    border: `1px solid ${selected ? color || "#ff6b35" : "#30363d"}`,
    borderRadius: 10, padding: "10px 12px", cursor: "pointer", marginBottom: 6,
    display: "flex", alignItems: "center", gap: 10, transition: "all .15s",
    boxShadow: selected ? `0 0 10px ${color || "#ff6b35"}44` : "none",
  });

  const inputStyle = {
    background: "#0d1117", border: "1px solid #30363d", borderRadius: 6,
    color: "#e6edf3", padding: "5px 10px", fontSize: 12, width: "100%",
    outline: "none",
  };

  const canSpin = hasSelection && !!targetItem && !spinning;

  return (
    <div style={{ maxWidth: 1140, margin: "0 auto", animation: "fadeIn .3s ease" }}>
      <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>⚡ 업그레이드</h2>
      <p style={{ color: "#8b949e", fontSize: 13, marginBottom: 16 }}>아이템을 여러 개 선택해 합산 가격으로 업그레이드하세요. 하우스 엣지 10%.</p>

      {/* ── 상단 미리보기: 선택 아이템 → 타겟 아이템 ── */}
      <div style={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 14, padding: "14px 18px", marginBottom: 20, display: "flex", alignItems: "center", gap: 14, minHeight: 88 }}>

        {/* 선택된 내 아이템들 */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, flex: 1 }}>
          {myItems.length === 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#484f58", fontSize: 13 }}>
              <span style={{ fontSize: 20 }}>🎒</span> 왼쪽에서 아이템을 선택하세요
            </div>
          ) : (
            <>
              {myItems.map((item, i) => (
                <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, background: "#0d1117", border: `1px solid ${item.color || "#30363d"}44`, borderRadius: 10, padding: "6px 10px", minWidth: 72 }}>
                  <SkinImage item={item} imageMap={imageMap} size={42} />
                  <div style={{ fontSize: 10, color: "#8b949e", textAlign: "center", maxWidth: 72, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayName(item.name)}</div>
                  <div style={{ fontSize: 11, color: "#3fb950", fontWeight: 700 }}>${item.price.toFixed(2)}</div>
                </div>
              ))}
              {myItems.length > 1 && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, paddingLeft: 4 }}>
                  <div style={{ fontSize: 10, color: "#8b949e" }}>합계</div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: "#ff6b35" }}>${myTotalPrice.toFixed(2)}</div>
                </div>
              )}
            </>
          )}
        </div>

        {/* 화살표 */}
        <div style={{ fontSize: 26, color: hasSelection ? "#ff6b35" : "#30363d", flexShrink: 0, transition: "color .2s" }}>→</div>

        {/* 타겟 아이템 */}
        {targetItem ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, background: "#0d1117", border: `1px solid ${targetItem.color || "#30363d"}`, borderRadius: 10, padding: "6px 10px", minWidth: 72, boxShadow: `0 0 14px ${targetItem.color || "#ff6b35"}33` }}>
            <SkinImage item={targetItem} imageMap={imageMap} size={42} />
            <div style={{ fontSize: 10, color: "#e6edf3", fontWeight: 600, textAlign: "center", maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayName(targetItem.name)}</div>
            <div style={{ fontSize: 11, color: "#3fb950", fontWeight: 700 }}>${targetItem.price.toFixed(2)}</div>
            {hasSelection && (
              <div style={{ fontSize: 10, color: "#f0a500", fontWeight: 700 }}>×{(targetItem.price / myTotalPrice).toFixed(2)}</div>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, background: "#0d1117", border: "1.5px dashed #30363d", borderRadius: 10, padding: "6px 10px", minWidth: 72, height: 82, color: "#484f58" }}>
            <span style={{ fontSize: 20 }}>🎯</span>
            <span style={{ fontSize: 10 }}>타겟 선택</span>
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 300px 1fr", gap: 20, alignItems: "start" }}>

        {/* ── 왼쪽: 내 인벤토리 ── */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#8b949e", textTransform: "uppercase", letterSpacing: 1 }}>
              🎒 내 아이템
            </div>
            {hasSelection && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, color: "#ff6b35", fontWeight: 700 }}>
                  {myItems.length}개 선택 · ${myTotalPrice.toFixed(2)}
                </span>
                <button
                  onClick={() => { setSelectedIdxs(new Set()); setTargetItem(null); setResult(null); }}
                  style={{ background: "none", border: "none", color: "#8b949e", fontSize: 12, cursor: "pointer", padding: "0 2px" }}
                >
                  ✕ 해제
                </button>
              </div>
            )}
          </div>
          <div style={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 14, padding: "14px 12px", height: 560, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
            {inventory.length === 0 && (
              <div style={{ color: "#484f58", fontSize: 13, textAlign: "center", margin: "auto" }}>인벤토리가 비어 있어요</div>
            )}
            {inventory.map((item, idx) => {
              const sel = selectedIdxs.has(idx);
              return (
                <div key={idx} style={cardBase(sel, item.color)} onClick={() => toggleItem(idx)}>
                  <SkinImage item={item} imageMap={imageMap} size={40} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: "#e6edf3", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{displayName(item.name)}</div>
                    <div style={{ fontSize: 13, color: "#3fb950", fontWeight: 700 }}>${item.price.toFixed(2)}</div>
                  </div>
                  {sel && <span style={{ color: "#ff6b35", fontSize: 14 }}>✓</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── 가운데: 휠 ── */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, paddingTop: 4 }}>

          {/* 승률 */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 34, fontWeight: 900, color: winChance > 0.5 ? "#3fb950" : winChance > 0.2 ? "#f0a500" : winChance > 0 ? "#f85149" : "#484f58" }}>
              {winChance > 0 ? `${(winChance * 100).toFixed(1)}%` : "—"}
            </div>
            <div style={{ fontSize: 11, color: "#8b949e" }}>승률</div>
          </div>

          {/* SVG 휠 */}
          <div style={{ position: "relative" }}>
            <svg width={SIZE} height={SIZE}>
              <circle cx={cx} cy={cy} r={R} fill="#0d1117" stroke="#30363d" strokeWidth={2} />
              {winDeg < 360 && <path d={arc(winDeg || 0.001, 360)} fill="rgba(248,81,73,0.22)" stroke="#f85149" strokeWidth={1} />}
              {winDeg > 0   && <path d={arc(0, winDeg)}           fill="rgba(63,185,80,0.22)"  stroke="#3fb950" strokeWidth={1} />}
              <circle cx={cx} cy={cy} r={R * 0.38} fill="#0d1117" stroke="#21262d" strokeWidth={2} />
              <g style={{ transform: `rotate(${needleDeg}deg)`, transformOrigin: `${cx}px ${cy}px`, transition: spinning ? "transform 3.8s cubic-bezier(0.15,0.6,0.1,1)" : "none" }}>
                <polygon points={`${cx},${cy - R + 6} ${cx - 5},${cy - R * 0.45} ${cx + 5},${cy - R * 0.45}`} fill="#fff" />
                <circle cx={cx} cy={cy} r={6} fill="#fff" />
              </g>
              {!spinning && result && (
                <text x={cx} y={cy + 5} textAnchor="middle" fill={result === "win" ? "#3fb950" : "#f85149"} fontSize={11} fontWeight="bold">
                  {result === "win" ? "SUCCESS" : "FAIL"}
                </text>
              )}
            </svg>
            <div style={{ position: "absolute", top: -8, left: "50%", transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "8px solid transparent", borderRight: "8px solid transparent", borderTop: "16px solid #fff" }} />
          </div>

          {/* 배율 빠른 선택 */}
          <div style={{ width: "100%" }}>
            <div style={{ fontSize: 11, color: "#8b949e", marginBottom: 6, textAlign: "center" }}>빠른 배율 선택</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6 }}>
              {MULTIPLIERS.map(({ label, value, color }) => {
                const targetPrice = hasSelection ? myTotalPrice * value : null;
                const closest     = targetPrice ? allCaseItems.reduce((b, i) => Math.abs(i.price - targetPrice) < Math.abs(b.price - targetPrice) ? i : b, allCaseItems[0] || { price: 0 }) : null;
                const isActive    = closest && targetItem?.name === closest.name;
                return (
                  <button
                    key={label}
                    onClick={() => handleMultiplier(value)}
                    disabled={!hasSelection}
                    style={{
                      background:   isActive ? `${color}22` : "#0d1117",
                      border:       `1px solid ${isActive ? color : "#30363d"}`,
                      borderRadius: 8, color: isActive ? color : hasSelection ? "#e6edf3" : "#484f58",
                      padding:      "7px 0", fontSize: 12, fontWeight: 700,
                      cursor:       hasSelection ? "pointer" : "not-allowed", transition: "all .15s",
                    }}
                    title={targetPrice ? `목표 $${targetPrice.toFixed(2)}` : ""}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 결과 */}
          {result && (
            <div style={{ fontWeight: 800, fontSize: 15, padding: "12px 18px", borderRadius: 12, color: result === "win" ? "#3fb950" : "#f85149", background: result === "win" ? "rgba(63,185,80,.12)" : "rgba(248,81,73,.12)", border: `1px solid ${result === "win" ? "#3fb950" : "#f85149"}`, animation: "fadeIn .3s ease", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, width: "100%" }}>
              {result === "win" && resultItem && (
                <SkinImage item={resultItem} imageMap={imageMap} size={64} />
              )}
              {result === "win" ? "🎉 업그레이드 성공!" : "💀 업그레이드 실패"}
              {result === "win" && resultItem && (
                <div style={{ fontSize: 12, color: "#e6edf3", fontWeight: 600 }}>{displayName(resultItem.name)}</div>
              )}
            </div>
          )}

          {/* 업그레이드 버튼 */}
          <button
            onClick={handleSpin}
            disabled={!canSpin}
            style={{
              width: "100%", border: "none", borderRadius: 10, fontWeight: 800, fontSize: 15, padding: "12px 0", transition: "all .2s",
              background: !canSpin ? "#21262d" : "linear-gradient(90deg,#ff6b35,#f7931e)",
              color:      !canSpin ? "#484f58" : "#fff",
              cursor:     !canSpin ? "not-allowed" : "pointer",
            }}
          >
            {spinning ? "⏳ 스핀 중..." : "⚡ 업그레이드"}
          </button>

          {/* 가격 요약 */}
          {hasSelection && targetItem && (
            <div style={{ fontSize: 11, color: "#8b949e", textAlign: "center", lineHeight: 1.8 }}>
              <span style={{ color: "#e6edf3", fontWeight: 700 }}>${myTotalPrice.toFixed(2)}</span>
              {myItems.length > 1 && <span style={{ color: "#484f58" }}> ({myItems.length}개)</span>}
              {" → "}
              <span style={{ color: "#e6edf3", fontWeight: 700 }}>${targetItem.price.toFixed(2)}</span>
              <span style={{ color: "#f0a500", marginLeft: 4 }}>×{(targetItem.price / myTotalPrice).toFixed(2)}</span>
            </div>
          )}
        </div>

        {/* ── 오른쪽: 타겟 아이템 ── */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#8b949e", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>🎯 타겟 아이템</div>

          {/* 가격 범위 필터 */}
          <div style={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 10, padding: "10px 12px", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, color: "#8b949e", whiteSpace: "nowrap" }}>가격 범위</span>
            <input
              type="number" min="0" placeholder="최소 $" value={priceMin}
              onChange={(e) => setPriceMin(e.target.value)}
              style={inputStyle}
            />
            <span style={{ color: "#484f58" }}>~</span>
            <input
              type="number" min="0" placeholder="최대 $" value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
              style={inputStyle}
            />
            {(priceMin || priceMax) && (
              <button onClick={() => { setPriceMin(""); setPriceMax(""); }} style={{ background: "none", border: "none", color: "#8b949e", fontSize: 14, cursor: "pointer", padding: "0 2px", flexShrink: 0 }}>✕</button>
            )}
          </div>

          {/* 아이템 목록 */}
          <div style={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 14, padding: "14px 12px", height: 490, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
            {filteredTargets.length === 0 && (
              <div style={{ color: "#484f58", fontSize: 13, textAlign: "center", margin: "auto" }}>해당 범위의 아이템이 없어요</div>
            )}
            {filteredTargets.map((item, idx) => {
              const sel      = targetItem?.name === item.name;
              const ratio    = hasSelection ? (myTotalPrice / item.price) * (1 - UPGRADE_HOUSE_EDGE) : null;
              const pct      = ratio !== null ? Math.min(90, ratio * 100) : null;
              const pctColor = pct === null ? "#8b949e" : pct > 50 ? "#3fb950" : pct > 20 ? "#f0a500" : "#f85149";
              return (
                <div key={idx} style={cardBase(sel, item.color)} onClick={() => { setTargetItem(sel ? null : item); setResult(null); }}>
                  <SkinImage item={item} imageMap={imageMap} size={40} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: "#e6edf3", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{displayName(item.name)}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
                      <span style={{ fontSize: 13, color: "#3fb950", fontWeight: 700 }}>${item.price.toFixed(2)}</span>
                      {pct !== null && (
                        <span style={{ fontSize: 10, color: pctColor, fontWeight: 700, background: `${pctColor}18`, padding: "1px 6px", borderRadius: 4 }}>
                          {pct.toFixed(1)}%
                        </span>
                      )}
                      {hasSelection && (
                        <span style={{ fontSize: 10, color: "#484f58" }}>×{(item.price / myTotalPrice).toFixed(1)}</span>
                      )}
                    </div>
                  </div>
                  {sel && <span style={{ color: "#ff6b35", flexShrink: 0 }}>✓</span>}
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  메인 앱
// ══════════════════════════════════════════════════════════════

export default function App() {
  const [loggedIn,  setLoggedIn]   = useState(false);
  const [steamUser, setSteamUser]  = useState(null);
  const [balance,   setBalance]    = useState(0);
  const [inventory, setInventory]  = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [totalPayout,  setTotalPayout]  = useState(0);

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showInventory, setShowInventory] = useState(false);

  // ── URL 기반 라우팅 ──────────────────────────────────────
  const { pathname } = useRouter();
  const caseSlug    = pathname.length > 1 ? pathname.slice(1) : null; // "/Neon-Case" → "Neon-Case"
  const [cases, setCases] = useState(BASE_CASES);
  const [priceLoadStatus, setPriceLoadStatus] = useState("idle"); // "idle"|"loading"|"done"|"error"
  const [priceError, setPriceError]           = useState("");     // 에러 상세 메시지
  const [skinImageMap, setSkinImageMap] = useState({});
  const [notification, setNotification] = useState(null);
  const [showDeposit,  setShowDeposit]  = useState(false);
  const [depositMode,  setDepositMode]  = useState("deposit");

  // ── 로그인 상태일 때만 해당 유저 DB에 자동 저장 ──────────
  useEffect(() => {
    if (!steamUser?.id) return;        // 로그인 전엔 저장하지 않음
    dbSave(steamUser.id, {
      balance,
      inventory,
      transactions,
      totalPayout,
    });
  }, [balance, inventory, transactions, totalPayout, steamUser]);

  // ── 실시간 히어로 통계 ───────────────────────────────────
  // activeUsers : Steam 로그인 기반 실제 접속자 수
  // totalPayout : 케이스에서 획득한 아이템 가치 + 출금액 실제 누적 (DB 복원)
  const [activeUsers, setActiveUsers] = useState(0);
  const caseCount = cases.length;

  // 로그인하면 +1, 탭 닫히거나 로그아웃하면 -1
  useEffect(() => {
    if (!loggedIn) return;
    setActiveUsers((u) => u + 1);
    return () => setActiveUsers((u) => Math.max(0, u - 1));
  }, [loggedIn]);

  // ── ByMykel CSGO-API로 스킨 + 스티커 이미지 로딩 ──────────
  useEffect(() => {
    const BASE = "https://raw.githubusercontent.com/ByMykel/CSGO-API/main/public/api/en";
    Promise.all([
      fetch(`${BASE}/skins.json`).then((r) => r.json()),
      fetch(`${BASE}/stickers.json`).then((r) => r.json()),
    ]).then(([skins, stickers]) => {
      const map = {};
      [...skins, ...stickers].forEach((item) => {
        if (!item.image) return;
        map[item.name] = item.image;
        const base = item.name.replace(/\s*\([^)]*\)\s*$/, "").trim();
        if (!map[base]) map[base] = item.image;
      });
      setSkinImageMap(map);
    }).catch(() => {});
  }, []);

  // imageMap: item.name → ByMykel 이미지 URL
  // 정확한 이름 → wear 제거 이름 순서로 폴백
  const imageMap = useMemo(() => {
    const map = {};
    BASE_CASES.forEach((c) => {
      c.items.forEach((item) => {
        const baseName = item.name.replace(/\s*\([^)]*\)\s*$/, "").trim();
        const url = skinImageMap[item.name] || skinImageMap[baseName];
        if (url) map[item.name] = url;
      });
    });
    return map;
  }, [skinImageMap]);

  // ── URL slug → 현재 케이스 결정 ────────────────────────
  const isProfilePage  = caseSlug === "profile";
  const isUpgradePage  = caseSlug === "upgrade";
  const selectedCase   = (!isProfilePage && !isUpgradePage && caseSlug)
    ? cases.find((c) => toSlug(c.name) === caseSlug) || null
    : null;
  const page = isProfilePage ? "profile"
             : isUpgradePage ? "upgrade"
             : selectedCase  ? "case-detail"
             : "cases";

  // ── Skinport 가격 로딩 (localStorage 1시간 캐시) ────────────
  // Vercel 배포 환경: CORS OK / 비인증 rate limit 분당 1회
  const loadPrices = useCallback(async () => {
    const CACHE_KEY = "mcc_prices_v1";
    const CACHE_TTL = 60 * 60 * 1000; // 1시간

    const applyPriceMap = (priceMap) => {
      setCases(
        BASE_CASES.map((c) => {
          const updatedItems = c.items.map((i) => ({
            ...i,
            price: priceMap[i.name] ?? i.price,
          }));
          const hasFreshPrices = updatedItems.some((i) => priceMap[i.name] != null);
          let newPrice = c.price;
          if (hasFreshPrices) {
            const totalChance = updatedItems.reduce((s, i) => s + i.chance, 0);
            const ev = updatedItems.reduce((s, i) => s + i.price * (i.chance / totalChance), 0);
            newPrice = Math.round((ev / (1 - c.edge / 100)) * 100) / 100;
          }
          return { ...c, price: newPrice, items: updatedItems };
        })
      );
      setPriceLoadStatus(Object.keys(priceMap).length > 0 ? "done" : "error");
    };

    const readCache = () => {
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const { ts, data } = JSON.parse(raw);
        if (Date.now() - ts > CACHE_TTL) return null;
        return data;
      } catch { return null; }
    };

    const writeCache = (priceMap) => {
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: priceMap }));
      } catch { /* 무시 */ }
    };

    // 캐시 유효하면 즉시 적용하고 종료
    const cached = readCache();
    if (cached && Object.keys(cached).length > 0) {
      applyPriceMap(cached);
      return;
    }

    setPriceLoadStatus("loading");
    setPriceError("");

    try {
      // 15초 타임아웃
      const controller = new AbortController();
      const tid = setTimeout(() => controller.abort(), 15000);

      // /api/prices: Vercel 서버리스 프록시 (CORS 우회)
      // 로컬 개발: src/setupProxy.js가 동일 경로 프록시 처리
      const r = await fetch("/api/prices", { signal: controller.signal });
      clearTimeout(tid);

      if (r.status === 429) {
        setPriceError("Skinport API 요청 한도 초과 (분당 1회). 잠시 후 재시도해주세요.");
        setPriceLoadStatus("error");
        console.error("[Price] Skinport 429 rate limited");
        return;
      }
      if (!r.ok) {
        setPriceError(`Skinport 서버 오류 (HTTP ${r.status}). 잠시 후 재시도해주세요.`);
        setPriceLoadStatus("error");
        console.error(`[Price] Skinport error ${r.status}`);
        return;
      }

      const items = await r.json();
      const priceMap = {};
      items.forEach((item) => {
        if (item.market_hash_name && item.min_price > 0)
          priceMap[item.market_hash_name] = item.min_price;
      });

      if (Object.keys(priceMap).length < 5) {
        setPriceError("Skinport 데이터가 비어있습니다. 잠시 후 재시도해주세요.");
        setPriceLoadStatus("error");
        return;
      }

      writeCache(priceMap);
      applyPriceMap(priceMap);
      console.info(`[Price] Skinport OK — ${Object.keys(priceMap).length}개 아이템`);

    } catch (e) {
      if (e.name === "AbortError") {
        setPriceError("Skinport 응답 시간 초과 (15초). 잠시 후 재시도해주세요.");
        console.error("[Price] Skinport timeout");
      } else {
        setPriceError("네트워크 오류로 가격을 불러올 수 없습니다. 잠시 후 재시도해주세요.");
        console.error("[Price] fetch error:", e.message);
      }
      setPriceLoadStatus("error");
    }
  }, []);

  useEffect(() => { loadPrices(); }, [loadPrices]);

  // ── 알림 ─────────────────────────────────────────────────
  const showNotif = (msg, color = "#3fb950") => {
    setNotification({ msg, color });
    setTimeout(() => setNotification(null), 3500);
  };

  // ── 회원 로그인 성공 처리 ─────────────────────────────────
  const handleLogin = () => setShowAuthModal(true);

  const handleAuthSuccess = async (record) => {
    // record: { id, email, username, ... } — Supabase users 테이블 row
    const userObj = {
      id:          record.id,          // Supabase UUID (dbLoad/dbSave 키)
      steamid:     record.username,
      personaname: record.username,
      email:       record.email,
    };

    // 해당 유저의 저장 데이터 불러오기 (신규면 기본값)
    const saved = await dbLoad(record.id);
    const isNew = saved.balance === 0 && saved.inventory.length === 0;

    setSteamUser(userObj);
    setLoggedIn(true);
    setBalance(saved.balance);
    setInventory(saved.inventory);
    setTransactions(saved.transactions);
    setTotalPayout(saved.totalPayout);
    setShowAuthModal(false);

    showNotif(
      isNew
        ? `${record.username}님, 처음 오셨군요! 환영합니다 🎉`
        : `${record.username}님, 다시 오셨네요! 🎮`,
      "#1f6feb"
    );
  };

  const handleUpdateBalance = (delta) =>
    setBalance((b) => Math.max(0, parseFloat((b + delta).toFixed(2))));

  const handleDeposit = (amount) => {
    handleUpdateBalance(amount);
    setTransactions((t) => [{ type: "deposit", amount: +amount, status: "완료", time: new Date().toLocaleTimeString() }, ...t]);
    showNotif(`$${amount.toFixed(2)} 입금 완료! 💰`, "#3fb950");
  };
  const handleWithdraw = (amount) => {
    handleUpdateBalance(-amount);
    // 출금액도 총 페이아웃에 누적
    setTotalPayout((p) => parseFloat((p + amount).toFixed(2)));
    setTransactions((t) => [{ type: "withdraw", amount: -amount, status: "처리중", time: new Date().toLocaleTimeString() }, ...t]);
    showNotif(`$${amount.toFixed(2)} 출금 신청 완료 📤`, "#1f6feb");
  };
  const handleAddTx = (tx) => setTransactions((t) => [tx, ...t]);
  const handleAddInventory = (item) => {
    setInventory((inv) => [item, ...inv]);
    // 케이스에서 획득한 아이템 가치 → 총 페이아웃 누적
    setTotalPayout((p) => parseFloat((p + item.price).toFixed(2)));
    showNotif(`${item.name} 획득! ($${item.price.toFixed(2)})`, item.color);
  };
  const handleSell = (idx, item) => {
    setInventory((inv) => inv.filter((_, i) => i !== idx));
    handleUpdateBalance(item.price);
    handleAddTx({ type: "sell", amount: +item.price, status: "완료", time: new Date().toLocaleTimeString() });
    showNotif(`판매 완료 +$${item.price.toFixed(2)}`, "#3fb950");
  };

  // ── 업그레이드 핸들러 ─────────────────────────────────
  const handleUpgradeWin = (myItems, targetItem) => {
    setInventory((inv) => {
      // 선택된 아이템들을 참조 기준으로 한 번씩 제거
      let next = [...inv];
      myItems.forEach((mi) => {
        const idx = next.indexOf(mi);
        if (idx !== -1) next.splice(idx, 1);
      });
      return [{ ...targetItem, acquiredAt: new Date().toISOString() }, ...next];
    });
    const totalIn = myItems.reduce((s, i) => s + i.price, 0);
    setTotalPayout((p) => parseFloat((p + targetItem.price).toFixed(2)));
    handleAddTx({ type: "upgrade-win", amount: parseFloat((targetItem.price - totalIn).toFixed(2)), label: `업그레이드 성공: ${displayName(targetItem.name)}`, date: new Date().toISOString() });
    showNotif(`🎉 업그레이드 성공! ${displayName(targetItem.name)} 획득`, "#3fb950");
  };

  const handleUpgradeLose = (myItems) => {
    setInventory((inv) => {
      let next = [...inv];
      myItems.forEach((mi) => {
        const idx = next.indexOf(mi);
        if (idx !== -1) next.splice(idx, 1);
      });
      return next;
    });
    const totalIn = myItems.reduce((s, i) => s + i.price, 0);
    const label = myItems.length === 1 ? displayName(myItems[0].name) : `${myItems.length}개 아이템`;
    handleAddTx({ type: "upgrade-lose", amount: -parseFloat(totalIn.toFixed(2)), label: `업그레이드 실패: ${label}`, date: new Date().toISOString() });
    showNotif(`💀 업그레이드 실패. ${label} 소멸`, "#f85149");
  };
  const openDeposit = (mode = "deposit") => { setDepositMode(mode); setShowDeposit(true); };

  return (
    <div style={{ minHeight: "100vh", background: "#0d1117", color: "#e6edf3", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        @keyframes slideIn { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: none; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #161b22; }
        ::-webkit-scrollbar-thumb { background: #30363d; border-radius: 3px; }
        input::-webkit-outer-spin-button, input::-webkit-inner-spin-button { -webkit-appearance: none; }
        input[type=number] { -moz-appearance: textfield; }
      `}</style>

      <Header
        balance={balance}
        inventoryValue={inventory.reduce((s, i) => s + i.price, 0)}
        loggedIn={loggedIn}
        steamUser={steamUser}
        onLogin={handleLogin}
        onOpenDeposit={openDeposit}
        loginLoading={false}
        onOpenInventory={() => setShowInventory(true)}
        onGoProfile={() => { navigate("/profile"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
        onGoHome={() => {
          navigate("/");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        onResetDb={() => {}}
      />

      {/* 알림 */}
      {notification && (
        <div style={{ position: "fixed", top: 80, right: 24, zIndex: 400, background: "#161b22", border: `1px solid ${notification.color}`, borderRadius: 10, padding: "12px 20px", color: notification.color, fontWeight: 600, animation: "slideIn .3s ease", maxWidth: 320, boxShadow: `0 4px 20px ${notification.color}44` }}>
          {notification.msg}
        </div>
      )}

      {/* 히어로 */}
      {!loggedIn && (
        <div style={{ background: "linear-gradient(135deg,#0d1117 0%,#1a1f2e 50%,#0d1117 100%)", textAlign: "center", padding: "60px 24px", borderBottom: "1px solid #30363d", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 0%,#ff6b3522 0%,transparent 70%)", pointerEvents: "none" }} />
          <h1 style={{ fontSize: "clamp(28px,5vw,52px)", fontWeight: 900, background: "linear-gradient(90deg,#ff6b35,#f7c59f,#ff6b35)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginBottom: 16 }}>CS 스킨 케이스 오픈 🎰</h1>
          <p style={{ color: "#8b949e", fontSize: 18, marginBottom: 32 }}>회원가입 후 케이스를 열어 희귀 스킨을 획득하세요!</p>
          <button onClick={() => setShowAuthModal(true)} style={{ background: "linear-gradient(90deg,#ff6b35,#f7931e)", border: "none", borderRadius: 12, color: "#fff", padding: "14px 36px", fontSize: 18, cursor: "pointer", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 10, boxShadow: "0 4px 20px #ff6b3544" }}>
            🔑 시작하기 (로그인 / 회원가입)
          </button>
          <div style={{ display: "flex", justifyContent: "center", gap: 40, marginTop: 40 }}>
            {[
              [activeUsers.toLocaleString(), "활성 유저"],
              [`$${totalPayout.toLocaleString("en-US", { maximumFractionDigits: 0 })}`, "총 페이아웃"],
              [caseCount.toString(), "케이스 종류"],
            ].map(([n, l]) => (
              <div key={l} style={{ textAlign: "center" }}>
                <div style={{ fontWeight: 800, fontSize: 24, color: "#ff6b35", transition: "all .3s" }}>{n}</div>
                <div style={{ color: "#8b949e", fontSize: 13 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 카테고리 탭 네비게이션 */}
      <div style={{ borderBottom: "1px solid #30363d", padding: "0 24px", display: "flex", alignItems: "center", gap: 4 }}>
        <button
          onClick={() => { navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          style={{
            background: "none", border: "none",
            borderBottom: `2px solid ${page === "cases" || page === "case-detail" ? "#ff6b35" : "transparent"}`,
            color: page === "cases" || page === "case-detail" ? "#ff6b35" : "#8b949e",
            padding: "14px 20px", cursor: "pointer", fontWeight: 600, fontSize: 14, transition: "all .2s",
          }}
        >
          🎁 케이스
        </button>

        <button
          onClick={() => { navigate("/upgrade"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          style={{
            background: "none", border: "none",
            borderBottom: `2px solid ${page === "upgrade" ? "#ff6b35" : "transparent"}`,
            color: page === "upgrade" ? "#ff6b35" : "#8b949e",
            padding: "14px 20px", cursor: "pointer", fontWeight: 600, fontSize: 14, transition: "all .2s",
          }}
        >
          ⚡ 업그레이드
        </button>

        {page === "case-detail" && selectedCase && (
          <>
            <span style={{ color: "#484f58", fontSize: 14 }}>›</span>
            <span style={{ borderBottom: "2px solid #ff6b35", color: "#ff6b35", padding: "14px 20px", fontWeight: 600, fontSize: 14 }}>
              {selectedCase.image} {selectedCase.name}
            </span>
          </>
        )}
      </div>

      {/* 메인 */}
      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px" }}>

        {/* ── 프로필 페이지 — URL: "/profile" ── */}
        {page === "profile" && loggedIn && (
          <ProfilePage
            steamUser={steamUser}
            balance={balance}
            inventory={inventory}
            transactions={transactions}
            onBack={() => { navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            onLogout={() => {
              setLoggedIn(false);
              setSteamUser(null);
              setBalance(0);
              setInventory([]);
              setTransactions([]);
              setTotalPayout(0);
              navigate("/");
              window.scrollTo({ top: 0, behavior: "smooth" });
              showNotif("로그아웃 되었습니다.", "#8b949e");
            }}
          />
        )}

        {/* ── 업그레이드 — URL: "/upgrade" ── */}
        {page === "upgrade" && (
          loggedIn ? (
            <UpgradePage
              inventory={inventory}
              cases={cases}
              imageMap={imageMap}
              onUpgradeWin={handleUpgradeWin}
              onUpgradeLose={handleUpgradeLose}
              loggedIn={loggedIn}
              onLoginRequired={() => setShowAuthModal(true)}
            />
          ) : (
            <div style={{ textAlign: "center", padding: "80px 24px", color: "#8b949e" }}>
              <div style={{ fontSize: 52, marginBottom: 16 }}>⚡</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#e6edf3", marginBottom: 8 }}>업그레이드는 로그인 후 이용 가능합니다</div>
              <button onClick={() => setShowAuthModal(true)} style={{ background: "linear-gradient(90deg,#ff6b35,#f7931e)", border: "none", borderRadius: 8, color: "#fff", padding: "10px 24px", fontWeight: 700, cursor: "pointer", marginTop: 16 }}>
                🔑 로그인 / 회원가입
              </button>
            </div>
          )
        )}

        {/* ── 케이스 목록 — URL: "/" ── */}
        {page === "cases" && (
          <>
            <div style={{ marginBottom: 28 }}>
              <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 6 }}>케이스 선택</h2>
              <p style={{ color: "#8b949e", fontSize: 14 }}>케이스를 선택하여 희귀 스킨을 노려보세요</p>
              {priceLoadStatus === "loading" && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 8, fontSize: 12, color: "#f0a500", background: "rgba(240,165,0,.12)", padding: "3px 10px", borderRadius: 20 }}>
                  💰 Skinport 최저가 불러오는 중…
                </span>
              )}
              {priceLoadStatus === "done" && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 8, fontSize: 12, color: "#3fb950", background: "rgba(63,185,80,.12)", padding: "3px 10px", borderRadius: 20 }}>
                  ✅ Skinport 실시간 최저가 적용됨
                </span>
              )}
              {priceLoadStatus === "error" && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#f85149", background: "rgba(248,81,73,.15)", border: "1px solid rgba(248,81,73,.4)", padding: "5px 12px", borderRadius: 20, fontWeight: 600 }}>
                    ❌ {priceError || "가격 호출 실패"}
                  </span>
                  <button
                    onClick={() => loadPrices()}
                    style={{ fontSize: 12, color: "#e6edf3", background: "#21262d", border: "1px solid #30363d", borderRadius: 20, padding: "5px 14px", cursor: "pointer", fontWeight: 600 }}
                  >
                    🔄 재시도
                  </button>
                </div>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 20 }}>
              {cases.map((c) => (
                <CaseCard
                  key={c.id}
                  c={c}
                  imageMap={imageMap}
                  priceLoadStatus={priceLoadStatus}
                  onSelect={(selected) => {
                    if (!loggedIn) { setShowAuthModal(true); return; }
                    navigate(`/${toSlug(selected.name)}`);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                />
              ))}
            </div>
          </>
        )}

        {/* ── 케이스 상세 — URL: "/Neon-Case" 등 ── */}
        {page === "case-detail" && selectedCase && (
          <CasePage
            c={selectedCase}
            onBack={() => { navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            balance={balance}
            onUpdateBalance={handleUpdateBalance}
            onAddInventory={handleAddInventory}
            onAddTx={handleAddTx}
            onSellItem={(item) => {
              setInventory((inv) => {
                const idx = inv.findLastIndex((i) => i.name === item.name);
                return idx !== -1 ? inv.filter((_, i) => i !== idx) : inv;
              });
              handleUpdateBalance(item.price);
              handleAddTx({ type: "sell", amount: +item.price, status: "완료", time: new Date().toLocaleTimeString() });
              showNotif(`💰 $${item.price.toFixed(2)} 판매 완료!`, "#238636");
            }}
            imageMap={imageMap}
            priceLoadStatus={priceLoadStatus}
          />
        )}

        {/* ── 404: 알 수 없는 URL ── */}
        {caseSlug && !selectedCase && (
          <div style={{ textAlign: "center", padding: "80px 24px", color: "#8b949e" }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>🔍</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#e6edf3", marginBottom: 8 }}>페이지를 찾을 수 없습니다</div>
            <button
              onClick={() => navigate("/")}
              style={{ background: "linear-gradient(90deg,#ff6b35,#f7c59f)", border: "none", borderRadius: 8, color: "#0d1117", padding: "10px 24px", fontWeight: 700, cursor: "pointer", marginTop: 16 }}
            >
              케이스 목록으로
            </button>
          </div>
        )}

      </main>

      {/* 로그인 / 회원가입 모달 */}
      {showAuthModal && (
        <LoginRegisterModal
          onClose={() => setShowAuthModal(false)}
          onLoginSuccess={handleAuthSuccess}
        />
      )}

      {/* 인벤토리 모달 */}
      {showInventory && loggedIn && (
        <InventoryModal
          inventory={inventory}
          onClose={() => setShowInventory(false)}
          onSell={(idx, item) => { handleSell(idx, item); }}
          onSellAll={() => {
            const total = inventory.reduce((s, i) => s + i.price, 0);
            handleUpdateBalance(total);
            handleAddTx({ type: "sell", amount: +total.toFixed(2), status: "완료", time: new Date().toLocaleTimeString() });
            setInventory([]);
            showNotif(`전체 판매 완료 +$${total.toFixed(2)}`, "#3fb950");
            setShowInventory(false);
          }}
          imageMap={imageMap}
        />
      )}

      {/* 입출금 모달 */}
      {showDeposit && loggedIn && (
        <DepositWithdrawModal mode={depositMode} balance={balance} onClose={() => setShowDeposit(false)}
          onDeposit={handleDeposit} onWithdraw={handleWithdraw} transactions={transactions} />
      )}

      <footer style={{ borderTop: "1px solid #30363d", padding: "24px", textAlign: "center", color: "#8b949e", fontSize: 12, marginTop: 60 }}>
        <div style={{ marginBottom: 6 }}>⚡ SKINVAULT — Steam OpenID 인증 · 공정한 랜덤 시스템 · 봇 거래 완전 차단</div>
        <div style={{ color: "#484f58" }}>이 사이트는 Valve Corporation과 제휴 관계가 없습니다. 스킨 이미지: bymykel/CSGO-database (MIT)</div>
      </footer>
    </div>
  );
}
